/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Table,
  Select,
  Modal,
  Input,
  Checkbox,
  InputNumber,
  Spin,
  Button,
} from "antd";
import "./product-modal.css";
import {
  getDataWithComp,
  getData,
  getAlert,
  deleteData,
  insertData,
} from "../../../utils/SamuiUtils";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import {
  updateItems,
  deleteItems,
  submitItems,
  deleteItem,
  bulkUpdateByCodeMaster,
  getItemsByCodeMaster,
} from "../../../utils/services";
import SmartSelect from "../../pages/InitialData/TbSetItem/SmartSelect";
import AddWarehouseModal from "./AddWarehouseModal";
import AddCategoryUnitModal from "./AddCategoryUnitModal";

const mapGroups = (rows = []) =>
  rows.map((r) => ({
    id: r.Group_Id ?? r.id ?? r.ID,
    name: r.Group_Name ?? r.name ?? r.Name,
    code:
      r.Group_Code ??
      r.Group_CodeName ??
      r.Code ??
      r.code ??
      "",
    unitId: r.Unit_Id ?? r.unitId ?? null,
    unitDefault:
      r.Unit_Default ?? r.Unit_Name ?? r.Group_Unit ?? r.Item_Unit_IN ?? "",
  }));

const getRowKey = (r) =>
  String(r.Item_Id ?? r.Item_Code ?? r.Item_Barcode ?? r.key);

const mapUnits = (rows = []) =>
  rows.map((r) => ({
    id: r.Unit_Id ?? r.id,
    name: r.Unit_Name ?? r.name,
  }));

const mapWarehouses = (rows = []) =>
  rows.map((r) => ({
    id: r.WH_Id ?? r.id,
    name: r.WH_Name ?? r.name,
    code: r.WH_Code ?? r.code ?? r.WH_Name ?? "",
  }));

const mapColours = (rows = []) =>
  rows.map((r) => ({
    id: r.Colour_Id ?? r.id,
    name: r.Colour_Name ?? r.name,
    code: r.Colour_Code ?? r.code ?? r.Colour_Name ?? "", // <— เพิ่มบรรทัดนี้
  }));

const mapSizes = (rows = []) =>
  rows
    .map((r) => ({
      id: r.Size_Id ?? r.id,
      name: r.Size_Name ?? r.name,
      code: r.Size_Code ?? r.code ?? r.Size_Name ?? "",
      line: Number(r.Size_Line ?? r.line ?? 999),
    }))
    .sort((a, b) => a.line - b.line);

const normalizeName = (name = "") =>
  name.trim().toLowerCase().replace(/\s+/g, " ");
const makeRowKey = () =>
  `manual-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const q = (s) => `'${String(s ?? "").replaceAll("'", "''")}'`;
const sanitizeNumeric = (raw) => {
  let s = (raw ?? "").toString().replace(/[^0-9.,]/g, "");
  const i = s.indexOf(".");
  if (i !== -1) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, "");
  return s;
};

const escapeRegExp = (value = "") =>
  String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const parserStrict = (v) => {
  let parsed = sanitizeNumeric(v).replace(/,/g, "");
  // จำกัดทศนิยมไม่เกิน 2 ตำแหน่ง
  const dotIndex = parsed.indexOf(".");
  if (dotIndex !== -1 && parsed.length > dotIndex + 3) {
    parsed = parsed.slice(0, dotIndex + 3);
  }
  return parsed;
};

const formatterWithCommas = (v) => {
  if (v === null || v === undefined || v === "") return "";

  const num = Number(v);
  if (isNaN(num)) return "";

  // จำกัดทศนิยมไม่เกิน 2 ตำแหน่ง
  const rounded = Math.round(num * 100) / 100;
  const s = String(rounded);
  const [int = "", dec] = s.split(".");
  const intFmt = int.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  // จำกัดทศนิยมให้แสดงแค่ 2 ตำแหน่ง (แสดงเฉพาะเมื่อมีทศนิยม)
  const decFmt = dec ? dec.slice(0, 2) : "";
  return decFmt ? `${intFmt}.${decFmt}` : intFmt;
};

const onlyNumberKeyDown = (e) => {
  const { key, ctrlKey, metaKey } = e;
  if (ctrlKey || metaKey) return;

  const allowNav = new Set([
    "Backspace",
    "Delete",
    "Tab",
    "Enter",
    "Escape",
    "Home",
    "End",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
  ]);
  if (allowNav.has(key)) return;

  if (/[0-9]/.test(key)) {
    // ตรวจสอบว่ามีทศนิยมเกิน 2 ตำแหน่งหรือไม่ - ห้ามพิมพ์ตั้งแต่ตอนพิมพ์เลย
    const el = e.currentTarget;
    const v = el?.value ?? "";
    const valueWithoutComma = v.replace(/,/g, "");
    const dotIdx = valueWithoutComma.indexOf(".");
    
    if (dotIdx !== -1) {
      // ถ้ามีจุดแล้ว ตรวจสอบตำแหน่ง cursor
      const { selectionStart, selectionEnd } = el;
      
      // หาตำแหน่ง cursor ใน value ที่ไม่มี comma
      let cursorPosClean = 0;
      const cursorPos = typeof selectionStart === "number" ? selectionStart : v.length;
      for (let i = 0; i < cursorPos && i < v.length; i++) {
        if (v[i] !== ",") cursorPosClean++;
      }
      
      // ตรวจสอบว่า cursor อยู่หลังจุดทศนิยมหรือไม่
      if (cursorPosClean > dotIdx) {
        const afterDot = valueWithoutComma.slice(dotIdx + 1);
        const cursorAfterDot = cursorPosClean - dotIdx - 1;
        
        // ถ้ามีทศนิยม 2 ตำแหน่งแล้ว และ cursor อยู่ที่ตำแหน่งที่ 3 หรือมากกว่า
        if (afterDot.length >= 2 && cursorAfterDot >= 2) {
          // ถ้าไม่ได้เลือกข้อความเพื่อแก้ไข ให้ block
          const isReplacing = 
            typeof selectionStart === "number" &&
            typeof selectionEnd === "number" &&
            selectionStart !== selectionEnd;
          
          if (!isReplacing) {
            e.preventDefault();
            return;
          }
        }
      }
    }
    return; // ตัวเลข
  }
  if (key === ",") return; // คอมมา
  if (key === ".") {
    // จุดเดียวเท่านั้น

    const el = e.currentTarget;
    const v = el?.value ?? "";
    const hasDot = v.includes(".");
    if (!hasDot) return;
    // ถ้า selection ครอบจุดอยู่ อนุญาตให้แทนที่ได้
    const dotIdx = v.indexOf(".");
    const { selectionStart, selectionEnd } = el;
    const coversDot =
      typeof selectionStart === "number" &&
      typeof selectionEnd === "number" &&
      selectionStart <= dotIdx &&
      dotIdx < selectionEnd;
    if (!coversDot) e.preventDefault();
    return;
  }
  e.preventDefault();
};

// ---------- หลัก ----------
export default function ProductModal({
  open,
  setOpen,
  mode = "create",
  initialData,
  onClose,
  onSubmit,
  onDataChanged,
  compactEditHeader = false,
}) {
  const isOpen = !!open;
  const origRowsRef = useRef([]);
  const focusKeyRef = useRef(null);
  const tableWrapRef = useRef(null);
  const shortNameDirtyRef = useRef(false);
  const unitDirtyRef = useRef(false);
  const baseCodeDirtyRef = useRef(false);
  const prevGroupRef = useRef(null);
  const [undeletableItemIds, setUndeletableItemIds] = useState(new Set());
  const [groups, setGroups] = useState([]);
  const [units, setUnits] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [colours, setColours] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [data, setData] = useState({
    Item_Group: "",
    Item_Short_Name: "",
    Item_Code_Master: "",
    Item_Name: "",
    Item_Name_EN: "",
    Item_Unit_IN: "",
    Item_Qty: "",
    Item_Is_Sale: "N",
    Item_Is_Service: "N",
    Item_Brand: "",
    Item_Series: "",
    Item_Cost: "",
    Item_Price: "",
    Item_PriceA: "",
    Item_PriceB: "",
    Item_Status: "Y",
  });

  const [whIds, setWhIds] = useState([]);
  const [selColourIds, setSelColourIds] = useState([]);
  const [selSizeIds, setSelSizeIds] = useState([]);
  const [initQty, setInitQty] = useState("");
  const [itemBaseCode, setItemBaseCode] = useState("");
  const [showAddCategoryUnitModal, setShowAddCategoryUnitModal] = useState(false);
  const [showAddUnitModal, setShowAddUnitModal] = useState(false);
  const [newUnitName, setNewUnitName] = useState("");
  const [isSavingUnit, setIsSavingUnit] = useState(false);
  const [showAddWarehouseModal, setShowAddWarehouseModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  // const [dupInfo, setDupInfo] = useState({ open: false, conflicts: [] }); // ไม่ใช้แล้ว แสดงแค่ alert
  // const [dupKeys, setDupKeys] = useState(new Set()); // ไม่ใช้แล้ว
  const originalHeaderDataRef = useRef(null); // เก็บค่าเดิมของ header เมื่อเปิด modal
  const [bulkUpdateInfo, setBulkUpdateInfo] = useState({
    open: false,
    changes: {},
    affectedItems: [],
  });

  const rowPricesBeforeChangeRef = useRef(null);
  const headerPriceBeforeChangeRef = useRef({
    Item_Price: null,
    Item_PriceA: null,
    Item_PriceB: null,
  });

  const cellRefMap = useRef({});
  const setCellRef = (rowKey, col) => (el) => {
    if (!rowKey) return;
    if (el) cellRefMap.current[`${rowKey}-${col}`] = el;
  };
  const focusCell = (rowKey, col) => {
    const el = cellRefMap.current[`${rowKey}-${col}`];
    el?.focus?.();
  };
  const rowArrowOrder = [
    "Item_Code",
    "Item_Name",
    "Brand",
    "Series",
    "Item_Qty",
    "Price1",
  ];
  const handleRowLeftRight = (e, rowKey, col) => {
    const idx = rowArrowOrder.indexOf(col);
    if (idx === -1) return false;
    if (e.key === "ArrowLeft" && idx > 0) {
      e.preventDefault();
      focusCell(rowKey, rowArrowOrder[idx - 1]);
      return true;
    }
    if (e.key === "ArrowRight" && idx < rowArrowOrder.length - 1) {
      e.preventDefault();
      focusCell(rowKey, rowArrowOrder[idx + 1]);
      return true;
    }
    return false;
  };

  const widthColumns = {
    rowNo: 50,
    code: 150,
    name: 300,
    brand: 120,
    series: 120,
    qty: 90,
    unit: 120,
    sale: 80,
    service: 80,
    colour: 90,
    size: 90,
    price1: 100,
    price2: 100,
    price3: 100,
    initQty: 100,
    cost: 100,
    delete: 100,
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setData((s) => ({ ...s, [name]: value }));

    if (mode === "edit" && name === "Item_Name") {
      const newName = String(value ?? "").trim();
      setRows((prev) =>
        prev.map((r) => (r.__deleted ? r : { ...r, Item_Name: newName }))
      );
    }
  };

  const handlePriceBlur = (field, newValue) => {
    console.debug("🔵 handlePriceBlur called:", { field, newValue });

    if (mode !== "edit") return;

    const fieldName =
      field === "Item_Price"
        ? "Price1"
        : field === "Item_PriceA"
        ? "Price2"
        : "Price3";

    const priceLabel =
      field === "Item_Price" ? "1" : field === "Item_PriceA" ? "2" : "3";

    const oldHeaderValue = Number(
      headerPriceBeforeChangeRef.current?.[field] ?? 0
    );
    const newHeaderValue = Number(newValue ?? 0);

    // ถ้าค่าเดิมและค่าใหม่เท่ากัน ไม่ต้องทำอะไร
    if (oldHeaderValue === newHeaderValue) return;

    // ✅ ใช้ snapshot ราคาของแถว (ก่อนเปลี่ยน) เพื่อเช็คว่าต้องถามหรือไม่
    // รูปแบบ: [{ key, value }]
    const snapshot = rowPricesBeforeChangeRef.current;
    if (!Array.isArray(snapshot) || snapshot.length === 0) return;

    // ถ้าทุกรายการมีค่าเท่ากับค่าใหม่อยู่แล้ว ก็ไม่ต้องถาม
    const hasDifferentPrices = snapshot.some(
      (x) => Number(x?.value ?? 0) !== newHeaderValue
    );
    if (!hasDifferentPrices) return;

    // ✅ สร้างข้อความตามที่ต้องการ (รองรับขึ้นบรรทัดใหม่)
    const msg =
      `การเปลี่ยนแปลงราคา ${priceLabel} เป็น ${formatterWithCommas(
        newHeaderValue
      )} บาท\n` +
      `จะอัพเดทราคา ${priceLabel} ในทุกรายการ (${snapshot.length} รายการ)\n\n` +
      `คุณยืนยันเปลี่ยนแปลงราคา ${priceLabel} หรือไม่?`;

    getAlert("C_DIALOG_MULTILINE", msg, (ok) => {
      if (ok) {
        setRows((prev) =>
          prev.map((r) =>
            r.__deleted ? r : { ...r, [fieldName]: newHeaderValue }
          )
        );
        headerPriceBeforeChangeRef.current[field] = newHeaderValue;
      } else {
        setData((s) => ({ ...s, [field]: oldHeaderValue }));
        const snapMap = new Map(
          snapshot.map((x) => [String(x.key), Number(x.value ?? 0)])
        );
        setRows((prev) =>
          prev.map((r) =>
            r.__deleted
              ? r
              : {
                  ...r,
                  [fieldName]: snapMap.get(String(r.key)) ?? r[fieldName],
                }
          )
        );
      }
    });
  };

  const selectedWarehouseNames = useMemo(
    () => warehouses.filter((w) => whIds.includes(w.id)).map((x) => x.name),
    [whIds, warehouses]
  );

  const onInitQtyCellChange = (rowKey, val) => {
    const num = Number(val || 0);
    setRows((prev) =>
      prev.map((r) =>
        r.key === rowKey ? { ...r, InitQty: num, __changed: true } : r
      )
    );
  };

  const onQtyCellChange = (rowKey, val) => {
    const num = Number(val || 0);
    setRows((prev) =>
      prev.map((r) =>
        r.key === rowKey ? { ...r, Item_Qty: num, __changed: true } : r
      )
    );
  };

  const onUnitCellChange = (rowKey, val) => {
    const next = val?.value ?? val?.label ?? val ?? "";
    setRows((prev) =>
      prev.map((r) =>
        r.key === rowKey
          ? { ...r, Item_Unit_IN: next, Unit: next, __changed: true }
          : r
      )
    );
  };

  const onCheckCellChange = (rowKey, field, checked) => {
    const next = checked ? "Y" : "N";
    setRows((prev) =>
      prev.map((r) =>
        r.key === rowKey ? { ...r, [field]: next, __changed: true } : r
      )
    );
  };

  const onTextCellChange = (rowKey, field, val) => {
    const next = String(val ?? "");
    setRows((prev) =>
      prev.map((r) => {
        if (r.key !== rowKey) return r;
        if (field === "Item_Code") {
          return {
            ...r,
            Item_Code: next,
            Item_Barcode: next,
            __changed: true,
          };
        }
        return { ...r, [field]: next, __changed: true };
      })
    );
  };

  const buildManualRow = (overrides = {}) => {
    const statusYN = data.Item_Status || "Y";
    return {
      key: makeRowKey(),
      Item_Id: null,
      __isNew: true,
      __manual: true,
      Item_Barcode: overrides.Item_Barcode ?? "",
      Item_Code: overrides.Item_Code ?? "",
      Item_Name: "",
      Item_Name_EN: "",
      Item_Unit_IN: data.Item_Unit_IN || "",
      Item_Qty: Number(data.Item_Qty || 0),
      Item_Is_Sale: data.Item_Is_Sale || "N",
      Item_Is_Service: data.Item_Is_Service || "N",
      Brand: data.Item_Brand || "",
      Series: data.Item_Series || "",
      Cost: Number(data.Item_Cost || 0),
      Price1: Number(data.Item_Price || 0),
      Price2: Number(data.Item_PriceA || 0),
      Price3: Number(data.Item_PriceB || 0),
      StatusYN: statusYN,
      Status: statusYN === "Y" ? "Active" : "In-Active",
      InitQty: Number(initQty || 0),
    };
  };

  const handleAddQuickRow = () => {
    const activeRows = rows.filter((r) => !r.__deleted);
    const firstCode = String(
      activeRows.find((r) => r.Item_Code)?.Item_Code || itemBaseCode || ""
    ).trim();

    let nextCode = "";
    if (firstCode) {
      const match = firstCode.match(/^(.*?)(\d+)$/);
      if (match) {
        const prefix = match[1];
        const width = match[2].length;
        const prefixRegex = new RegExp(`^${escapeRegExp(prefix)}(\\d+)$`);
        const maxNum = activeRows.reduce((max, r) => {
          const code = String(r.Item_Code || "").trim();
          const m = code.match(prefixRegex);
          if (!m) return max;
          const num = parseInt(m[1], 10);
          return Number.isNaN(num) ? max : Math.max(max, num);
        }, parseInt(match[2], 10));
        const nextNum = Number.isNaN(maxNum) ? 1 : maxNum + 1;
        nextCode = `${prefix}${String(nextNum).padStart(width, "0")}`;
      } else {
        nextCode = `${firstCode}-0001`;
      }
    }

    setRows((prev) => [
      ...prev,
      buildManualRow(
        nextCode ? { Item_Code: nextCode, Item_Barcode: nextCode } : {}
      ),
    ]);
  };

  const columns = useMemo(() => {
    const hasRows = rows.length > 0;
    const w = (k) => widthColumns[k];
    return [
      {
        title: "#",
        dataIndex: "_row",
        width: w("rowNo"),
        align: "center",
        ...(hasRows && { fixed: "left" }),
        render: (_, __, i) => i + 1,
      },
      {
        title: "รหัส",
        dataIndex: "Item_Code",
        key: "code",
        width: w("code"),
        align: "left",
        ...(hasRows && { fixed: "left" }),
        render: (i, record) => {
          const isEditable = mode === "create" || record.__isNew;
          return (
            <div
              className="pm-code-cell"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 2,
              }}
            >
              {isEditable ? (
                <Input
                  size="small"
                  value={record.Item_Code || ""}
                  placeholder="รหัสสินค้า"
                  ref={setCellRef(record.key, "Item_Code")}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) =>
                    onTextCellChange(record.key, "Item_Code", e.target.value)
                  }
                  onKeyDown={(e) => handleRowLeftRight(e, record.key, "Item_Code")}
                />
              ) : (
                <span
                  style={{
                    fontSize: "small",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    wordWrap: "break-word",
                    textAlign: "left",
                    width: "100%",
                  }}
                >
                  {i}
                </span>
              )}
              {record.__isNew && (
                <span className="badge-new pm-code-badge">NEW</span>
              )}
            </div>
          );
        },
      },

      {
        title: "ชื่อไทย",
        dataIndex: "Item_Name",
        key: "name",
        width: w("name"),
        ...(hasRows && { fixed: "left" }),
        render: (_, record) => {
          const isEditable = true;
          if (isEditable) {
            return (
              <Input
                size="small"
                value={record.Item_Name || ""}
                placeholder="ชื่อสินค้า"
                ref={setCellRef(record.key, "Item_Name")}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) =>
                  onTextCellChange(record.key, "Item_Name", e.target.value)
                }
                onKeyDown={(e) => handleRowLeftRight(e, record.key, "Item_Name")}
              />
            );
          }
          // ✅ แสดง Item_Name โดยตรง (ชื่อมีสี+ไซส์อยู่แล้วจาก buildVariantRow)
          return record.Item_Name || "";
        },
      },
      {
        title: "ยี่ห้อ",
        dataIndex: "Brand",
        key: "brand",
        width: w("brand"),
        align: "center",
        render: (_, record) => {
          const isEditable = true;
          if (isEditable) {
            return (
              <Input
                size="small"
                value={record.Brand || ""}
                placeholder="ยี่ห้อ"
                ref={setCellRef(record.key, "Brand")}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) =>
                  onTextCellChange(record.key, "Brand", e.target.value)
                }
                onKeyDown={(e) => handleRowLeftRight(e, record.key, "Brand")}
              />
            );
          }
          return record.Brand || "";
        },
      },
      {
        title: "รุ่น",
        dataIndex: "Series",
        key: "series",
        width: w("series"),
        align: "center",
        render: (_, record) => {
          const isEditable = true;
          if (isEditable) {
            return (
              <Input
                size="small"
                value={record.Series || ""}
                placeholder="รุ่น"
                ref={setCellRef(record.key, "Series")}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) =>
                  onTextCellChange(record.key, "Series", e.target.value)
                }
                onKeyDown={(e) => handleRowLeftRight(e, record.key, "Series")}
              />
            );
          }
          return record.Series || "";
        },
      },
      {
        title: "ปริมาณ",
        dataIndex: "Item_Qty",
        key: "qty",
        width: w("qty"),
        align: "right",
        onHeaderCell: () => ({ style: { textAlign: "center" } }),
        render: (_, record) => {
          const isEditable = true;
          if (isEditable) {
            return (
              <InputNumber
                size="small"
                style={{ width: "100%" }}
                inputStyle={{ textAlign: "right" }}
                value={record.Item_Qty ?? 0}
                keyboard={false}
                controls={false}
                ref={setCellRef(record.key, "Item_Qty")}
                onChange={(v) => onQtyCellChange(record.key, v ?? 0)}
                onFocus={selectOnFocus}
                formatter={formatterWithCommas}
                parser={parserStrict}
                precision={2}
                inputMode="decimal"
                onKeyDown={(e) => {
                  if (handleRowLeftRight(e, record.key, "Item_Qty")) return;
                  onlyNumberKeyDown(e);
                }}
              />
            );
          }
          return Number(record.Item_Qty || 0).toLocaleString();
        },
      },
      {
        title: (
          <div style={{ position: "relative", width: "100%", height: "100%" }}>
            <span
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                whiteSpace: "nowrap",
              }}
            >
              หน่วย
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setNewUnitName("");
                setShowAddUnitModal(true);
              }}
              style={{
                position: "absolute",
                top: "50%",
                right: 0,
                border: "none",
                background: "#f97316",
                color: "white",
                padding: "0 6px",
                height: 18,
                borderRadius: 4,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                transform: "translateY(-50%)",
              }}
              title="เพิ่มหน่วยใหม่"
            >
              <PlusOutlined />
            </button>
          </div>
        ),
        dataIndex: "Item_Unit_IN",
        key: "unit",
        width: w("unit"),
        align: "center",
        render: (_, record) => {
          const isEditable = true;
          if (isEditable) {
            return (
              <SmartSelect
                className="w-100"
                style={{ width: "100%", height: "42px" }}
                allowClear
                placeholder="หน่วย"
                value={record.Item_Unit_IN || record.Unit || ""}
                onChange={(v) => onUnitCellChange(record.key, v)}
                options={units
                  .sort((a, b) => a.name.localeCompare(b.name, "th"))
                  .map((u) => ({
                    label: u.name,
                    value: u.name,
                    id: u.id,
                  }))}
                maxRender={120}
                debounceMs={180}
                returnObject
              />
            );
          }
          return record.Item_Unit_IN || record.Unit || "";
        },
      },
      {
        title: "เพื่อขาย",
        dataIndex: "Item_Is_Sale",
        key: "sale",
        width: w("sale"),
        align: "center",
        render: (_, record) => {
          const isEditable = true;
          if (isEditable) {
            return (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Checkbox
                  checked={String(record.Item_Is_Sale) === "Y"}
                  onChange={(e) =>
                    onCheckCellChange(
                      record.key,
                      "Item_Is_Sale",
                      e.target.checked
                    )
                  }
                />
              </div>
            );
          }
          return String(record.Item_Is_Sale) === "Y" ? "Y" : "N";
        },
      },
      {
        title: "เพื่อใช้",
        dataIndex: "Item_Is_Service",
        key: "service",
        width: w("service"),
        align: "center",
        render: (_, record) => {
          const isEditable = true;
          if (isEditable) {
            return (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Checkbox
                  checked={String(record.Item_Is_Service) === "Y"}
                  onChange={(e) =>
                    onCheckCellChange(
                      record.key,
                      "Item_Is_Service",
                      e.target.checked
                    )
                  }
                />
              </div>
            );
          }
          return String(record.Item_Is_Service) === "Y" ? "Y" : "N";
        },
      },

      {
        title: "ราคา 1",
        dataIndex: "Price1",
        key: "p1",
        width: w("price1"),
        align: "right",
        ...(hasRows && { fixed: "left" }),
        onHeaderCell: () => ({ style: { textAlign: "center" } }),
        render: (_, record) => (
          <InputNumber
            size="small"
            style={{ width: "100%" }}
            inputStyle={{ textAlign: "right" }}
            value={record.Price1 ?? 0}
            keyboard={false}
            controls={false}
            ref={setCellRef(record.key, "Price1")}
            onChange={(v) => onPriceCellChange(record.key, "Price1", v ?? 0)}
            onFocus={selectOnFocus}
            formatter={formatterWithCommas}
            parser={parserStrict}
            onKeyDown={(e) => {
              if (handleRowLeftRight(e, record.key, "Price1")) return;
              const activeRows = rows.filter((r) => !r.__deleted);
              const idx = activeRows.findIndex((r) => r.key === record.key);

              if (e.key === "ArrowUp" && idx > 0) {
                e.preventDefault();
                focusCell(activeRows[idx - 1].key, "Price1");
                return;
              }
              if (e.key === "ArrowDown" && idx < activeRows.length - 1) {
                e.preventDefault();
                focusCell(activeRows[idx + 1].key, "Price1");
                return;
              }
              if (e.key === "ArrowRight") {
                e.preventDefault();
                focusCell(record.key, "Price2");
                return;
              }
              onlyNumberKeyDown(e);
            }}
          />
        ),
      },
      {
        title: "ราคา 2",
        dataIndex: "Price2",
        key: "p2",
        width: w("price2"),
        align: "right",
        ...(hasRows && { fixed: "left" }),
        onHeaderCell: () => ({ style: { textAlign: "center" } }),
        render: (_, record) => (
          <InputNumber
            size="small"
            style={{ width: "100%" }}
            inputStyle={{ textAlign: "right" }}
            value={record.Price2 ?? 0}
            keyboard={false}
            controls={false}
            ref={setCellRef(record.key, "Price2")}
            onChange={(v) => onPriceCellChange(record.key, "Price2", v ?? 0)}
            onFocus={selectOnFocus}
            formatter={formatterWithCommas}
            parser={parserStrict}
            onKeyDown={(e) => {
              const activeRows = rows.filter((r) => !r.__deleted);
              const idx = activeRows.findIndex((r) => r.key === record.key);

              if (e.key === "ArrowLeft") {
                e.preventDefault();
                focusCell(record.key, "Price1");
                return;
              }

              if (e.key === "ArrowRight") {
                e.preventDefault();
                focusCell(record.key, "Price3");
                return;
              }

              if (e.key === "ArrowUp" && idx > 0) {
                e.preventDefault();
                focusCell(activeRows[idx - 1].key, "Price2");
                return;
              }

              if (e.key === "ArrowDown" && idx < activeRows.length - 1) {
                e.preventDefault();
                focusCell(activeRows[idx + 1].key, "Price2");
                return;
              }
              onlyNumberKeyDown(e);
            }}
          />
        ),
      },
      {
        title: "ราคา 3",
        dataIndex: "Price3",
        key: "p3",
        width: w("price3"),
        align: "right",
        onHeaderCell: () => ({ style: { textAlign: "center" } }),
        render: (_, record) => (
          <InputNumber
            size="small"
            style={{ width: "100%" }}
            inputStyle={{ textAlign: "right" }}
            value={record.Price3 ?? 0}
            keyboard={false}
            controls={false}
            ref={setCellRef(record.key, "Price3")}
            onChange={(v) => onPriceCellChange(record.key, "Price3", v ?? 0)}
            onFocus={selectOnFocus}
            formatter={formatterWithCommas}
            parser={parserStrict}
            onKeyDown={(e) => {
              const activeRows = rows.filter((r) => !r.__deleted);
              const idx = activeRows.findIndex((r) => r.key === record.key);

              if (e.key === "ArrowLeft") {
                e.preventDefault();
                focusCell(record.key, "Price2");
                return;
              }

              if (e.key === "ArrowRight") {
                e.preventDefault();
                focusCell(record.key, "InitQty");
                return;
              }

              if (e.key === "ArrowUp" && idx > 0) {
                e.preventDefault();
                focusCell(activeRows[idx - 1].key, "Price3");
                return;
              }

              if (e.key === "ArrowDown" && idx < activeRows.length - 1) {
                e.preventDefault();
                focusCell(activeRows[idx + 1].key, "Price3");
                return;
              }

              onlyNumberKeyDown(e);
            }}
          />
        ),
      },
      {
        title: "จน.ตั้งต้น",
        dataIndex: "InitQty",
        key: "initEdit",
        width: w("initQty"),
        align: "right",
        onHeaderCell: () => ({ style: { textAlign: "center" } }),
        render: (_, record) => {
          const isEditable = mode === "create" || record.__isNew;

          if (isEditable) {
            return (
              <InputNumber
                style={{
                  width: "100%",
                }}
                size="small"
                inputStyle={{ textAlign: "right" }}
                value={record.InitQty ?? 0}
                keyboard={false}
                controls={false}
                ref={setCellRef(record.key, "InitQty")}
                onClick={(e) => e.stopPropagation()}
                onChange={(v) => onInitQtyCellChange(record.key, v ?? 0)}
                onFocus={selectOnFocus}
                formatter={formatterWithCommas}
                parser={parserStrict}
                precision={0}
                min={0}
                inputMode="numeric"
                onKeyDown={(e) => {
                  const activeRows = rows.filter((r) => !r.__deleted);
                  const idx = activeRows.findIndex((r) => r.key === record.key);

                  if (e.key === "ArrowLeft") {
                    e.preventDefault();
                    focusCell(record.key, "Price3");
                    return;
                  }
                  if (e.key === "ArrowUp" && idx > 0) {
                    e.preventDefault();
                    focusCell(activeRows[idx - 1].key, "InitQty");
                    return;
                  }
                  if (e.key === "ArrowDown" && idx < activeRows.length - 1) {
                    e.preventDefault();
                    focusCell(activeRows[idx + 1].key, "InitQty");
                    return;
                  }
                  onlyNumberKeyDown(e);
                }}
              />
            );
          }
          return (
            <span style={{ paddingRight: "8px" }}>
              {Number(record.InitQty || "").toLocaleString()}
            </span>
          );
        },
      },
      {
        title: "ต้นทุน",
        dataIndex: "Cost",
        key: "cost",
        width: w("cost"),
        align: "center",
        render: (_, record) => (
          <InputNumber
            size="small"
            style={{ width: "100%" }}
            inputStyle={{ textAlign: "right" }}
            value={record.Cost ?? 0}
            keyboard={false}
            controls={false}
            onChange={(v) => onPriceCellChange(record.key, "Cost", v ?? 0)}
            onFocus={selectOnFocus}
            formatter={formatterWithCommas}
            parser={parserStrict}
            precision={2}
            inputMode="decimal"
            onKeyDown={onlyNumberKeyDown}
          />
        ),
      },

      {
        title: "ลบ",
        key: "delete",
        width: w("delete"),
        align: "center",
        ...(hasRows && { fixed: "right" }),
        render: (_, record) => {
          const isUndeletable =
            record.Item_Id && undeletableItemIds.has(String(record.Item_Id));
          const canDelete = !isUndeletable && !isLoading;

          return (
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              disabled={!canDelete}
              onClick={() => handleDeleteRow(record)}
            />
          );
        },
      },
    ];
  }, [rows, undeletableItemIds, isLoading, units, mode]);

  const onPriceCellChange = (rowKey, priceField, val) => {
    const num = Number(val || 0);
    setRows((prev) =>
      prev.map((r) =>
        r.key === rowKey ? { ...r, [priceField]: num, __changed: true } : r
      )
    );
  };

  const buildVariantRow = ({ baseShort, colour, size, idx, whText }) => {
    console.debug("buildVariantRow data : ", initialData);
    const colorCode = colour?.code || "";
    const colorName = colour?.name || "";
    const sizeCode = size?.code || "";
    const sizeName = size?.name || "";
    const code = `${baseShort}${colorCode}${sizeCode}`;
    const statusYN = data.Item_Status || "Y";
    // ✅ ใช้ data.Item_Name ใน create mode, initialData?.header?.Item_Short_Name2 ใน edit mode
    const baseName = mode === "create" 
      ? (data.Item_Name || "")
      : (initialData?.header?.Item_Short_Name2 || "");
    
    // ✅ ตรวจสอบว่าชื่อมีสี/ไซส์อยู่แล้วหรือไม่
    const normalizeText = (text) =>
      String(text || "")
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim();
    
    const baseNameLower = normalizeText(baseName);
    const hasColour = colorName && baseNameLower.includes(normalizeText(colorName));
    const hasSize = sizeName && baseNameLower.includes(normalizeText(sizeName));
    
    const parts = [baseName];
    if (colorName && !hasColour) parts.push(colorName);
    if (sizeName && !hasSize) parts.push(sizeName);
    const fullItemName = parts.join(" ").trim();

    return {
      key: `${code}-${idx}`,
      Item_Id: null,
      __isNew: true,
      Item_Barcode: code,
      Item_Code: code,
      Item_Name: fullItemName, // ✅ ใช้ชื่อเต็มที่บวกแล้ว
      Item_Name_EN: "",
      Unit: data.Item_Unit_IN || "",
      Item_Unit_IN: data.Item_Unit_IN || "",
      Item_Qty: data.Item_Qty || 0,
      Item_Is_Sale: data.Item_Is_Sale || "N",
      Item_Is_Service: data.Item_Is_Service || "N",
      Colour: colorName,
      Size: sizeName,
      Size_Name: sizeName,
      Brand: data.Item_Brand || "",
      Series: data.Item_Series || "",
      Warehouses: whText,
      InitQty: Number(initQty || 0),
      Cost: Number(data.Item_Cost || 0),
      Price1: 0,
      Price2: 0,
      Price3: 0,
      StatusYN: statusYN,
      Status: statusYN === "Y" ? "Active" : "In-Active",
    };
  };

  const totalColumnWidth = Object.values(widthColumns).reduce(
    (sum, width) => sum + width,
    0
  );

  function splitChanges(currentRows = []) {
    const original = Array.isArray(origRowsRef.current)
      ? origRowsRef.current
      : [];

    const normId = (v) => (v == null ? null : String(v));
    const toNum = (v) => Number(v ?? 0);
    const toStr = (v) => String(v ?? "");

    const origById = new Map(
      original
        .filter((r) => r?.Item_Id != null)
        .map((r) => [normId(r.Item_Id), r])
    );

    const nowRows = Array.isArray(currentRows) ? currentRows : [];
    const nowActive = nowRows.filter((r) => !r?.__deleted);
    const nowDeleted = nowRows.filter((r) => r?.__deleted);

    const toInsert = [];
    const toUpdate = [];
    const toDelete = [];

    // ✅ DELETE เฉพาะแถวที่ถูก mark __deleted เท่านั้น
    for (const r of nowDeleted) {
      const id = normId(r?.Item_Id);
      if (id && origById.has(id)) toDelete.push(origById.get(id));
    }

    // ✅ INSERT / UPDATE ใช้ Item_Id เป็นหลักเท่านั้น (ไม่อิง Item_Code/key)
    for (const n of nowActive) {
      const id = normId(n?.Item_Id);

      if (!id) {
        toInsert.push(n);
        continue;
      }

      const o = origById.get(id);
      if (!o) {
        // กัน edge: มี id แต่ไม่เจอใน snapshot → treat เป็น insert แบบปลอดภัย
        toInsert.push({ ...n, Item_Id: null });
        continue;
      }

      const before = {
        InitQty: toNum(o?.InitQty ?? o?.Init_Qty),
        StatusYN: toStr(o?.StatusYN ?? o?.Item_Status ?? "Y"),
        Cost: toNum(o?.Cost ?? o?.Item_Cost),
        Price1: toNum(o?.Price1 ?? o?.Item_Price),
        Price2: toNum(o?.Price2 ?? o?.Item_PriceA),
        Price3: toNum(o?.Price3 ?? o?.Item_PriceB),
        Brand: toStr(o?.Brand ?? o?.Item_Brand),
        Series: toStr(o?.Series ?? o?.Item_Series),
        Unit: toStr(o?.Item_Unit_IN ?? o?.Unit),
        Name: toStr(o?.Item_Name),
        Qty: toNum(o?.Item_Qty ?? 0),
        IsSale: toStr(o?.Item_Is_Sale ?? "N"),
        IsService: toStr(o?.Item_Is_Service ?? "N"),
      };

      const after = {
        InitQty: toNum(n?.InitQty ?? n?.Init_Qty),
        StatusYN: toStr(n?.StatusYN ?? n?.Item_Status ?? "Y"),
        Cost: toNum(n?.Cost ?? n?.Item_Cost),
        Price1: toNum(n?.Price1 ?? n?.Item_Price),
        Price2: toNum(n?.Price2 ?? n?.Item_PriceA),
        Price3: toNum(n?.Price3 ?? n?.Item_PriceB),
        Brand: toStr(n?.Brand ?? n?.Item_Brand),
        Series: toStr(n?.Series ?? n?.Item_Series),
        Unit: toStr(n?.Item_Unit_IN ?? n?.Unit),
        Name: toStr(n?.Item_Name),
        Qty: toNum(n?.Item_Qty ?? 0),
        IsSale: toStr(n?.Item_Is_Sale ?? "N"),
        IsService: toStr(n?.Item_Is_Service ?? "N"),
      };

      const changed =
        before.InitQty !== after.InitQty ||
        before.StatusYN !== after.StatusYN ||
        before.Cost !== after.Cost ||
        before.Price1 !== after.Price1 ||
        before.Price2 !== after.Price2 ||
        before.Price3 !== after.Price3 ||
        before.Brand !== after.Brand ||
        before.Series !== after.Series ||
        before.Unit !== after.Unit ||
        before.Name !== after.Name ||
        before.Qty !== after.Qty ||
        before.IsSale !== after.IsSale ||
        before.IsService !== after.IsService;

      if (changed || n?.__changed) {
        toUpdate.push({ ...n, Item_Id: o.Item_Id });
      }
    }

    // กันซ้ำ delete
    const seen = new Set();
    const dedupDelete = [];
    for (const d of toDelete) {
      const id = normId(d?.Item_Id);
      if (!id || seen.has(id)) continue;
      seen.add(id);
      dedupDelete.push(d);
    }

    return { original, toInsert, toUpdate, toDelete: dedupDelete };
  }

  const mapRowToDetail = (r, whIds) => {
    const fullName = r.Item_Name || "";
    const colour = r.Colour || r.Item_Colour || "";
    const size = r.Size_Name || r.Size || r.Item_Size || "";

    return {
      Item_Barcode: r.Item_Barcode,
      Item_Code: r.Item_Code,
      Item_Name: fullName,
      Item_Unit_IN: r.Item_Unit_IN ?? r.Unit,
      Item_Qty: r.Item_Qty ?? 0,
      Item_Is_Sale: r.Item_Is_Sale ?? "N",
      Item_Is_Service: r.Item_Is_Service ?? "N",
      Item_Colour: colour,
      Item_Size: size,
      Item_Brand: r.Brand,
      Item_Series: r.Series || "",
      Item_Cost: r.Cost,
      Item_Price: r.Price1,
      Item_PriceA: r.Price2,
      Item_PriceB: r.Price3,
      Item_Status: r.StatusYN,
      Init_Qty: r.InitQty,
      WH_Ids: whIds,
    };
  };

  const buildHeader = (data, shortName, whIds, initQty, itemBaseCode) => ({
    Item_Group: data.Item_Group,
    Item_Short_Name: shortName,
    Item_Name: data.Item_Name,
    Item_Name_EN: data.Item_Name_EN,
    Item_Unit_IN: data.Item_Unit_IN,
    Item_Qty: data.Item_Qty || 0,
    Item_Is_Sale: data.Item_Is_Sale || "N",
    Item_Is_Service: data.Item_Is_Service || "N",
    Item_Brand: data.Item_Brand,
    Item_Series: data.Item_Series || "",
    Item_Cost: Number(data.Item_Cost || 0),
    Item_Price: Number(data.Item_Price || 0),
    Item_PriceA: Number(data.Item_PriceA || 0),
    Item_PriceB: Number(data.Item_PriceB || 0),
    Item_Status: data.Item_Status,
    WH_Ids: whIds,
    Init_Qty_All: Number(initQty || 0),
    itemBaseCode,
  });

  const handleDeleteSelected = async () => {
    if (selectedRowKeys.length === 0) {
      return getAlert("FAILED", "กรุณาเลือกรายการที่จะลบ");
    }

    try {
      const selectedRows = rows.filter((x) =>
        selectedRowKeys.includes(getRowKey(x))
      );

      const result = await deleteItems(selectedRows, {
        checkOnly: true,
        silent: true,
      });
      const { canDelete, cannotDelete } = result;
      if (cannotDelete.length > 0 && canDelete.length === 0) {
        getAlert("WARNING_LIST", {
          title: "ไม่สามารถลบสินค้าได้ เนื่องจากมีการเคลื่อนไหวสต็อกแล้ว",
          items: cannotDelete.map((r) => ({
            code: r.Item_Code,
            name: r.Item_Name,
          })),
        });
        return;
      }
      if (cannotDelete.length > 0 && canDelete.length > 0) {
        getAlert(
          "WARNING_LIST_CONFIRM",
          {
            title: "มีสินค้าบางรายการไม่สามารถลบได้",
            cannotDelete: cannotDelete.map((r) => ({
              code: r.Item_Code,
              name: r.Item_Name,
            })),
            canDelete: canDelete.map((r) => ({
              code: r.Item_Code,
              name: r.Item_Name,
            })),
            question: "คุณต้องการลบสินค้าที่เหลือหรือไม่?",
          },
          async (ok) => {
            if (!ok) return;

            try {
              for (const r of canDelete) {
                const where = `WHERE Item_Id='${r.Item_Id}'`;
                await Promise.all([
                  deleteData("Tb_Set_Item", where),
                  deleteData("WH_ITEM_STC", where),
                  deleteData("WH_ITEM_Onhand", where),
                ]);
              }
              const deletedKeys = canDelete.map((r) => getRowKey(r));
              setRows((prev) =>
                prev.filter((r) => !deletedKeys.includes(getRowKey(r)))
              );
              setSelectedRowKeys([]);
              getAlert("OK", `ลบข้อมูลสำเร็จ ${canDelete.length} รายการ`);
              await onDataChanged?.();
            } catch (e) {
              console.error(e);
              getAlert("FAILED", "ไม่สามารถลบข้อมูลได้");
            }
          }
        );
        return;
      }
      getAlert("C_DIALOG", "คุณต้องการลบรายการที่เลือกหรือไม่?", async (ok) => {
        if (!ok) return;
        try {
          for (const r of canDelete) {
            const where = `WHERE Item_Id='${r.Item_Id}'`;
            await Promise.all([
              deleteData("Tb_Set_Item", where),
              deleteData("WH_ITEM_STC", where),
              deleteData("WH_ITEM_Onhand", where),
            ]);
          }

          setRows((prev) =>
            prev.filter((r) => !selectedRowKeys.includes(getRowKey(r)))
          );
          setSelectedRowKeys([]);
          await onDataChanged?.();
        } catch (e) {
          console.error(e);
          getAlert("FAILED", "ไม่สามารถลบข้อมูลได้");
        }
      });
    } catch (e) {
      console.error(e);
      getAlert("FAILED", "เกิดข้อผิดพลาดในการตรวจสอบข้อมูล");
    }
  };

  const selectOnFocus = (e) => {
    setTimeout(() => e?.target?.select?.(), 0);
  };

  const handleDeleteRow = async (record) => {
    if (record.Item_Id && undeletableItemIds.has(String(record.Item_Id))) {
      getAlert(
        "WARNING",
        "ไม่สามารถลบสินค้าได้ เนื่องจากมีการเคลื่อนไหวสต็อกแล้ว"
      );
      return;
    }
    if (!record.Item_Id) {
      setRows((prev) => {
        const remaining = prev.filter((r) => r.key !== record.key);
        if (remaining.length === 0) {
          setTimeout(() => {
            setOpen(false);
          }, 100);
        }

        return remaining;
      });
      return;
    }
    const confirmMessage = `คุณต้องการลบรายการ\n"${record.Item_Name}"\nใช่หรือไม่?`;
    getAlert("C_DIALOG_MULTILINE", confirmMessage, async (ok) => {
      if (!ok) return;

      try {
        setIsLoading(true);

        const result = await deleteItem(record, { silent: true });

        if (result.cannotDelete.length > 0) {
          getAlert("WARNING_LIST", {
            title: "ไม่สามารถลบสินค้าได้ เนื่องจากมีการเคลื่อนไหวสต็อกแล้ว",
            items: result.cannotDelete.map((r) => ({
              code: r.Item_Code,
              name: r.Item_Name,
            })),
          });
          return;
        }

        setRows((prev) => {
          const remaining = prev.filter((r) => r.key !== record.key);
          if (remaining.length === 0) {
            setTimeout(() => {
              setOpen(false);
              onDataChanged?.(); // รีเฟรชข้อมูล
            }, 100);
          }

          return remaining;
        });
        await onDataChanged?.();
      } catch (err) {
        console.error("handleDeleteRow error:", err);
        getAlert("FAILED", "ไม่สามารถลบรายการได้");
      } finally {
        setIsLoading(false);
      }
    });
  };

  const submitAll = async () => {
    const confirmMsg =
      mode === "create"
        ? "คุณต้องการสร้างสินค้าใหม่หรือไม่?"
        : "คุณต้องการบันทึกการเปลี่ยนแปลงหรือไม่?";

    const handleDuplicate = (res) => {
      const conflicts = res?.conflicts || [];
      
      // แสดง alert เพื่อแจ้งเตือนผู้ใช้ทันที
      const conflictCodes = conflicts
        .map((c) => c.Item_Code || c.Item_Barcode || "ไม่ระบุ")
        .join(", ");
      getAlert("FAILED", `พบข้อมูลซ้ำ: ${conflictCodes} กรุณาแก้ไขก่อนบันทึก`);
    };

    // helper: validate ก่อนยิงจริง
    const validateBeforeSubmit = () => {
      // ---------- CREATE: บังคับฟิลด์สำคัญ ----------
      if (mode === "create") {
        if (!data.Item_Group) {
          getAlert("FAILED", "กรุณาเลือกหมวดหมู่สินค้า");
          return false;
        }
        if (!whIds || whIds.length === 0) {
          getAlert("FAILED", "กรุณาเลือกคลัง");
          return false;
        }
      }

      const allRows = Array.isArray(rows) ? rows : [];
      const activeRows = allRows.filter((r) => !r?.__deleted);

      // ---------- EDIT: กันเคสลบหมด/rows หาย ----------
      if (mode === "edit") {
        if (activeRows.length === 0) {
          // ❗ แนะนำ: ไม่อนุญาตให้ลบหมดใน edit เพื่อกันพลาด
          getAlert(
            "FAILED",
            "ไม่พบรายการสินค้าในตาราง (โหมดแก้ไขไม่อนุญาตให้ลบทั้งหมด)"
          );
          return false;
        }
      }

      // ---------- CREATE: ถ้าไม่มีแถวในตาราง ให้ block ----------
      if (mode === "create" && activeRows.length === 0) {
        getAlert("FAILED", "ไม่พบรายการสินค้าในตาราง");
        return false;
      }

      // ---------- กันแถวที่ code ว่าง ----------
      const missing = activeRows.find(
        (r) => !String(r?.Item_Code || r?.Item_Barcode || "").trim()
      );
      if (missing) {
        getAlert("FAILED", "บางรายการไม่มีรหัสสินค้า (Item_Code)");
        return false;
      }

      return true;
    };

    if (!validateBeforeSubmit()) {
      console.log("Validation failed");
      return;
    }

    // ฟังก์ชันสำหรับบันทึกข้อมูล
    const performSubmit = async () => {
      setIsLoading(true);
      try {
        // ======================
        // CREATE MODE
        // ======================
        if (mode === "create") {
          console.debug("rows ::::", rows);
          console.debug("whIds ::::", whIds);
          console.debug("data ::::", data);

          const payload = {
            mode,
            header: buildHeader(
              data,
              itemBaseCode,
              whIds,
              initQty,
              itemBaseCode
            ),
            details: (rows || [])
              .filter((r) => !r.__deleted)
              .map((r) => mapRowToDetail(r, whIds)),
          };
          console.debug("payload", payload);
          const res = await submitItems(payload);
          console.debug("submitItems response", res);

          if (res?.ok === false && res?.errorCode === "DUPLICATE") {
            handleDuplicate(res);
            setIsLoading(false);
            return;
          }
          if (res?.ok === false) {
            console.error("submitItems failed:", res);
            getAlert("FAILED", res?.message || "บันทึกไม่สำเร็จ");
            setIsLoading(false);
            return;
          }

          // ✅ ส่ง response กลับไปให้ onDataChanged ใช้ (เพิ่มข้อมูลคลังที่เพิ่มสินค้า)
          setOpen(false);
          await onDataChanged?.({
            ...res,
            whIds: whIds || [], // ส่งข้อมูลคลังที่เพิ่มสินค้า
          });
          return;
        }

        // ======================
        // EDIT MODE
        // ======================
        if (mode === "edit") {
          const original = originalHeaderDataRef.current || {};
          const changes = {};

          if (
            String(data.Item_Name || "").trim() !==
            String(original.Item_Name || "").trim()
          ) {
            changes.Item_Name = String(data.Item_Name || "").trim();
          }
          if (
            String(data.Item_Brand || "").trim() !==
            String(original.Item_Brand || "").trim()
          ) {
            changes.Item_Brand = String(data.Item_Brand || "").trim();
          }
          if (
            String(data.Item_Series || "").trim() !==
            String(original.Item_Series || "").trim()
          ) {
            changes.Item_Series = String(data.Item_Series || "").trim();
          }
          if (
            String(data.Item_Group || "") !== String(original.Item_Group || "")
          ) {
            changes.Item_Group = String(data.Item_Group || "");
          }

          if (Object.keys(changes).length > 0 && itemBaseCode) {
            const affectedItems = await getItemsByCodeMaster(itemBaseCode);
            const currentIds = new Set(
              rows.filter((r) => !r.__deleted).map((r) => r.Item_Id)
            );
            const otherItems = affectedItems.filter(
              (item) => !currentIds.has(item.Item_Id)
            );

            if (otherItems.length > 0) {
              setBulkUpdateInfo({
                open: true,
                changes,
                affectedItems: otherItems,
              });
              setIsLoading(false);
              return;
            }
          }

          const { toInsert, toUpdate, toDelete } = splitChanges(rows);
          let canDelete = [];
          let cannotDelete = [];

          if (toDelete.length > 0) {
            const check = await deleteItems(toDelete, { checkOnly: true });
            canDelete = check?.canDelete || [];
            cannotDelete = check?.cannotDelete || [];

            // ถ้ามีรายการที่ลบไม่ได้
            if (cannotDelete.length > 0) {
              // กรณีลบไม่ได้ทั้งหมด
              if (canDelete.length === 0) {
                getAlert(
                  "WARNING_LIST",
                  {
                    title:
                      "ไม่สามารถลบสินค้าได้ เนื่องจากมีการเคลื่อนไหวสต็อกแล้ว",
                    items: cannotDelete.map((r) => ({
                      code: r.Item_Code,
                      name: r.Item_Name,
                    })),
                  },
                  async () => {
                    // ปิด modal + รีเฟรช
                    setOpen(false);
                    await onDataChanged?.();
                  }
                );
                return;
              }

              getAlert(
                "WARNING_LIST",
                {
                  title:
                    "มีสินค้าบางรายการไม่สามารถลบได้ เนื่องจากมีการเคลื่อนไหวสต็อกแล้ว",
                  items: cannotDelete.map((r) => ({
                    code: r.Item_Code,
                    name: r.Item_Name,
                  })),
                },
                async () => {}
              );
            }
          }

          if (canDelete.length > 0) {
            await deleteItems(canDelete, { silent: true });
          }

          if (toUpdate.length > 0) {
            await updateItems(toUpdate);
          }

          if (toInsert.length > 0) {
            const whForEdit = initialData?.header?.WH_Ids ?? whIds ?? [];
            const payload = {
              mode: "create",
              header: buildHeader(
                data,
                data.Item_Name,
                whForEdit,
                initQty,
                itemBaseCode
              ),
              details: toInsert.map((r) => mapRowToDetail(r, whForEdit)),
            };

            const res = await submitItems(payload);

            if (res?.ok === false && res?.errorCode === "DUPLICATE") {
              handleDuplicate(res);
              return;
            }
            if (res?.ok === false) return;
          }

          setOpen(false);
          await onDataChanged?.();
          return;
        }
      } catch (e) {
        console.error(e);
        getAlert("FAILED", "บันทึกไม่สำเร็จ");
      } finally {
        setIsLoading(false);
      }
    };

    // ถ้าเป็นโหมดสร้างใหม่ ไม่ต้องถามยืนยัน ให้บันทึกเลย
    if (mode === "create") {
      performSubmit();
      return;
    }

    // ถ้าเป็นโหมดแก้ไข ยังคงถามยืนยันอยู่
    console.log("Showing confirmation dialog:", confirmMsg);
    getAlert("C_DIALOG", confirmMsg, async (ok) => {
      console.log("Confirmation dialog result:", ok);
      if (!ok) {
        console.log("User cancelled");
        return;
      }
      performSubmit();
    });
  };

  /* =========================================================
   * 1) LOAD MASTER DATA (groups / units / wh / colours / sizes)
   * =======================================================*/
  const [isMasterLoading, setIsMasterLoading] = useState(false);
  useEffect(() => {
    if (!isOpen) return;

    (async () => {
      setIsMasterLoading(true);
      try {
        const [g, u, w, c, s] = await Promise.all([
          getDataWithComp("Tb_Set_ItmGroup", "AND Group_Status='Y'"),
          getDataWithComp("Tb_Set_Unit", "AND Unit_Status='Y' ORDER BY Unit_Name"),
          getDataWithComp("Tb_Set_WH", "AND WH_Status='Y'"),
          getDataWithComp("Tb_Set_ItmColour", "AND Colour_Status='Y'"),
          getDataWithComp("Tb_Set_ItmSize", "AND Size_Status='Y'"),
        ]);

        const unitRows = Array.isArray(u) ? u : u?.data || [];
        setGroups(mapGroups(g || []));
        setUnits(mapUnits(unitRows || []));
        setWarehouses(mapWarehouses(w || []));
        setColours(mapColours(c || []));
        setSizes(mapSizes(s || []));
      } catch (e) {
        console.error(e);
      } finally {
        setIsMasterLoading(false);
      }
    })();
  }, [isOpen]);

  // ฟังก์ชันรีเฟรชข้อมูล
  const refreshUnits = async () => {
    const u = await getDataWithComp(
      "Tb_Set_Unit",
      "AND Unit_Status = 'Y' ORDER BY Unit_Name"
    );
    const unitRows = Array.isArray(u) ? u : u?.data || [];
    setUnits(mapUnits(unitRows || []));
  };

  const handleCreateUnit = async () => {
    const unitName = String(newUnitName || "").trim();
    if (!unitName) {
      getAlert("FAILED", "กรุณากรอกชื่อหน่วย");
      return;
    }

    setIsSavingUnit(true);
    try {
      const compId = localStorage.getItem("company");
      if (!compId) {
        getAlert("FAILED", "ไม่พบ Company ID");
        setIsSavingUnit(false);
        return;
      }
      const fields = "Unit_Name,Unit_Status,Comp_Id";
      const valuesArray = [[unitName, "Y", Number(compId)]];
      const response = await insertData("Tb_Set_Unit", fields, valuesArray);

      if (response && response.ok !== false) {
        await refreshUnits();
        setShowAddUnitModal(false);
        setNewUnitName("");
        getAlert("OK", "เพิ่มหน่วยสำเร็จ");
      } else {
        const errorMsg =
          response?.error || response?.message || "ไม่สามารถเพิ่มหน่วยได้";
        getAlert("FAILED", `เกิดข้อผิดพลาด: ${errorMsg}`);
      }
    } catch (error) {
      const errorMsg =
        error?.response?.data?.error ||
        error?.message ||
        "เกิดข้อผิดพลาดในการเชื่อมต่อกับเซิร์ฟเวอร์";
      getAlert("FAILED", `เกิดข้อผิดพลาด: ${errorMsg}`);
    } finally {
      setIsSavingUnit(false);
    }
  };

  const refreshGroups = async () => {
    const g = await getDataWithComp("Tb_Set_ItmGroup", "AND Group_Status='Y'");
    const mapped = mapGroups(g || []);
    setGroups(mapped);
    return mapped;
  };

  const refreshWarehouses = async () => {
    const w = await getDataWithComp("Tb_Set_WH", "AND WH_Status='Y'");
    setWarehouses(mapWarehouses(w || []));
  };

  /* =========================================================
   * 2) INIT MODAL (CREATE / EDIT)
   *    - EDIT = โหลดเฉพาะ "กลุ่มที่กด"
   *    - ✅ ตรวจสอบรายการที่ลบไม่ได้ทันที
   * =======================================================*/
  useEffect(() => {
    if (!isOpen) return;

    /* ================= CREATE ================= */
    if (mode === "create") {
      setRows([]);
      setSelectedRowKeys([]);
      focusKeyRef.current = null;
      origRowsRef.current = [];
      shortNameDirtyRef.current = false;
      unitDirtyRef.current = false;
      setUndeletableItemIds(new Set());

      setSelColourIds([]);
      setSelSizeIds([]);

      setData({
        Item_Group: "",
        Item_Short_Name: "",
        Item_Name: "",
        Item_Name_EN: "",
        Item_Unit_IN: "",
        Item_Qty: "",
        Item_Is_Sale: "N",
        Item_Is_Service: "N",
        Item_Brand: "",
        Item_Series: "",
        Item_Cost: "",
        Item_Price: "",
        Item_PriceA: "",
        Item_PriceB: "",
        Item_Status: "Y",
      });

      setInitQty("");
      setWhIds([]);
      baseCodeDirtyRef.current = false;
      setItemBaseCode("");

      return;
    }

    /* ================= EDIT ================= */
    if (mode !== "edit" || !initialData) return;

    (async () => {
      setIsLoading(true);

      try {
        /* ---------- header ---------- */
        const baseName = initialData.header?.Item_Short_Name ?? "";

        setData({
          Item_Group: initialData.header?.Item_Group ?? "",
          Item_Short_Name: initialData.header?.Item_Short_Name ?? "",
          Item_Name: baseName,
          Item_Name_EN: initialData.header?.Item_Name_EN ?? "",
          Item_Unit_IN: initialData.header?.Item_Unit_IN ?? "",
          Item_Qty: initialData.header?.Item_Qty ?? "",
          Item_Is_Sale: initialData.header?.Item_Is_Sale ?? "N",
          Item_Is_Service: initialData.header?.Item_Is_Service ?? "N",
          Item_Brand: initialData.header?.Item_Brand ?? "",
          Item_Series: initialData.header?.Item_Series ?? "",
          Item_Cost: initialData.header?.Item_Cost ?? "",
          Item_Price: initialData.header?.Item_Price ?? "",
          Item_PriceA: initialData.header?.Item_PriceA ?? "",
          Item_PriceB: initialData.header?.Item_PriceB ?? "",
          Item_Status: initialData.header?.Item_Status ?? "Y",
        });
        originalHeaderDataRef.current = {
          Item_Name: baseName,
          Item_Brand: initialData.header?.Item_Brand ?? "",
          Item_Series: initialData.header?.Item_Series ?? "",
          Item_Group: initialData.header?.Item_Group ?? "",
        };

        setWhIds(initialData.header?.WH_Ids || []);
        setInitQty(initialData.header?.Init_Qty_All ?? "");

        const dets = initialData.details || [];
        const rawFk = String(initialData.__focusKey ?? "");

        const found = dets.find(
          (d) =>
            String(d.Item_Id) === rawFk ||
            String(d.Item_Code) === rawFk ||
            String(d.Item_Barcode) === rawFk
        );

        focusKeyRef.current = found
          ? String(found.Item_Id ?? found.Item_Code ?? found.Item_Barcode)
          : rawFk;

        const extractBaseName = (fullName) => {
          if (!fullName) return "";

          let baseName = fullName;
          // baseName = baseName.replace(/\s+สี[^\s]+/g, "");
          baseName = baseName.replace(
            /\s+(ไซต์\s*)?([SMLX]{1,3}|[0-9]{1,2}XL|[0-9]+|[A-Z]{1,2}S?)\s*$/g,
            ""
          );
          // baseName = baseName.replace(/\s+[A-Z]{1,3}\s*$/g, "");

          return baseName.trim();
        };

        const baseNameToFind = extractBaseName(
          found?.Item_Name || initialData.header?.Item_Name || ""
        );
        const scoped = baseNameToFind
          ? dets.filter((d) => {
              const dBaseName = extractBaseName(d.Item_Name || "");
              return dBaseName === baseNameToFind;
            })
          : dets;
        const seenNames = new Set();
        const getSizeLine = (row) => {
          const sizeVal = row.Item_Size ?? row.Size ?? "";
          const found = sizes.find(
            (s) => s.code === sizeVal || s.name === sizeVal
          );
          const lineValue = found?.line ?? 999;
          return lineValue;
        };

        const uniqueScoped = scoped
          .filter((d) => {
            const key = normalizeName(d.Item_Name);
            if (seenNames.has(key)) return false;
            seenNames.add(key);
            return true;
          })
          .sort((a, b) => {
            const getBaseName = (fullName) => {
              let base = fullName;
              base = base.replace(/\s+สี[^\s]+/g, "");
              base = base.replace(
                /\s+(ไซต์\s*)?([SMLX]{1,3}|[0-9]{1,2}XL|[0-9]+|[A-Z]{1,2}S?)\s*$/g,
                ""
              );
              return base.trim();
            };

            const nameA = getBaseName((a.Item_Name || "").toString().trim());
            const nameB = getBaseName((b.Item_Name || "").toString().trim());
            const nameCompare = nameA.localeCompare(nameB, "th", {
              sensitivity: "base",
            });
            if (nameCompare !== 0) return nameCompare;

            const brandA = (a.Brand || a.Item_Brand || "").toString().trim();
            const brandB = (b.Brand || b.Item_Brand || "").toString().trim();
            const brandCompare = brandA.localeCompare(brandB, "th", {
              sensitivity: "base",
            });

            if (brandCompare !== 0) return brandCompare;

            const seriesA = (a.Series || a.Item_Series || "").toString().trim();
            const seriesB = (b.Series || b.Item_Series || "").toString().trim();
            const seriesCompare = seriesA.localeCompare(seriesB, "th", {
              sensitivity: "base",
            });
            if (seriesCompare !== 0) return seriesCompare;

            const colourA = (a.Colour || a.Item_Colour || "").toString().trim();
            const colourB = (b.Colour || b.Item_Colour || "").toString().trim();
            const colourCompare = colourA.localeCompare(colourB, "th", {
              sensitivity: "base",
            });
            if (colourCompare !== 0) return colourCompare;

            const lineA = getSizeLine(a);
            const lineB = getSizeLine(b);
            const result = lineA - lineB;
            return result;
          });
        const itemIds = uniqueScoped
          .map((d) => d.Item_Id)
          .filter((id) => id != null && id !== undefined);

        const result = await deleteItems(
          itemIds.map((id) => ({ Item_Id: id })),
          { checkOnly: true, silent: true }
        );

        const cannotDeleteIds = new Set(
          result.cannotDelete.map((r) => String(r.Item_Id))
        );
        setUndeletableItemIds(cannotDeleteIds);

        const mappedRows = uniqueScoped.map((d, i) => {
          const sizeCode = d.Item_Size ?? d.Size ?? "";
          const sizeObj = sizes.find(
            (s) => s.code === sizeCode || s.name === sizeCode
          );
          const sizeName = sizeObj?.name;

          return {
            ...d,
            Item_Id: d.Item_Id ?? null,
            key: String(d.Item_Id ?? d.Item_Code ?? d.Item_Barcode ?? i),
            __deleted: false,
            Price1: Number(d.Price1 ?? d.Item_Price ?? 0),
            Price2: Number(d.Price2 ?? d.Item_PriceA ?? 0),
            Price3: Number(d.Price3 ?? d.Item_PriceB ?? 0),
            Item_Unit_IN: d.Unit ?? d.Item_Unit_IN ?? "",
            Item_Qty:
              d.Item_Qty ??
              initialData?.header?.Item_Qty ??
              0,
            Item_Is_Sale:
              d.Item_Is_Sale ??
              initialData?.header?.Item_Is_Sale ??
              "N",
            Item_Is_Service:
              d.Item_Is_Service ??
              initialData?.header?.Item_Is_Service ??
              "N",
            Size: sizeName,
            Size_Name: sizeName,
          };
        });

        setRows(mappedRows);
        setSelectedRowKeys([]);
        origRowsRef.current = scoped.map((x) => ({ ...x }));
        shortNameDirtyRef.current = false;
        unitDirtyRef.current = false;
        baseCodeDirtyRef.current = false;

        if (mode === "edit" && uniqueScoped.length > 0) {
          const firstRow = uniqueScoped[0];
          if (firstRow.Item_Code_Master) {
            setItemBaseCode(String(firstRow.Item_Code_Master).trim());
          } else {
            setItemBaseCode(firstRow.Item_Code_Master);
          }
        } else if (mode === "create") {
          setItemBaseCode("");
        }
      } catch (err) {
        console.error("useEffect [EDIT] error:", err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [isOpen, mode, initialData, sizes]);

  /* =========================================================
   * 3) STATUS -> rows
   * =======================================================*/
  useEffect(() => {
    const yn = data.Item_Status || "Y";
    setRows((prev) =>
      prev.map((r) => ({
        ...r,
        StatusYN: yn,
        Status: yn === "Y" ? "Active" : "In-Active",
      }))
    );
  }, [data.Item_Status]);

  /* =========================================================
   * 4) (EDIT) ดึง ids ของสี/ไซส์จาก details -> selColourIds/selSizeIds
   * =======================================================*/
  useEffect(() => {
    if (!isOpen || mode !== "edit" || !initialData) return;
    if (!colours.length && !sizes.length) return;

    const dets = initialData.details || [];
    const cIds = Array.from(
      new Set(
        dets
          .map((d) => {
            const val = d.Item_Colour ?? d.Colour ?? "";
            return colours.find((c) => c.name === val || c.code === val)?.id;
          })
          .filter((id) => id != null)
      )
    );
    const sIds = Array.from(
      new Set(
        dets
          .map((d) => {
            const val = d.Item_Size ?? d.Size ?? "";
            return sizes.find((s) => s.name === val || s.code === val)?.id;
          })
          .filter((id) => id != null)
      )
    );

    setSelColourIds([]);
    setSelSizeIds([]);

    // จากนั้นค่อย set ตาม initialData
    if (cIds.length) setSelColourIds(cIds);
    if (sIds.length) setSelSizeIds(sIds);
  }, [isOpen, mode, initialData, colours, sizes]);

  /* =========================================================
   * 5) Auto หน่วย และ รหัสสินค้า จาก group
   *    - หน่วยต้องมาจาก Tb_Set_ItmGroup.Unit_Id (ทั้ง create และ edit)
   *    - ✅ itemBaseCode auto-fill เฉพาะ CREATE (ป้องกันเปลี่ยนโดยไม่ตั้งใจใน EDIT)
   * =======================================================*/
  useEffect(() => {
    if (!isOpen) return;
    if (!data.Item_Group) return;
    if (!groups.length || !units.length) return;

    const grp = groups.find((g) => String(g.id) === String(data.Item_Group));
    if (!grp) return;

    const grpUnitId =
      grp.Unit_Id ?? grp.unitId ?? grp.unit_id ?? grp.unitID ?? null;

    const unitNameFromGroup =
      grpUnitId != null
        ? units.find((u) => String(u.id) === String(grpUnitId))?.name
        : "";

    if (unitNameFromGroup) {
      setData((prev) => ({
        ...prev,
        Item_Unit_IN: unitNameFromGroup,
      }));
    }
  }, [isOpen, data.Item_Group, groups, units]);

  /* =========================================================
   * 5.1) Auto รหัสสินค้า จาก select (หมวดหมู่ + หน่วย)
   *    - ทำเฉพาะ CREATE และไม่ทับเมื่อผู้ใช้แก้มือ
   * =======================================================*/
  useEffect(() => {
    if (!isOpen || mode !== "create") return;
    const nextGroup = String(data.Item_Group || "");
    if (nextGroup && nextGroup !== String(prevGroupRef.current || "")) {
      baseCodeDirtyRef.current = false;
      prevGroupRef.current = nextGroup;
    }
    if (baseCodeDirtyRef.current) return;
    if (!data.Item_Group) return;

    const grp = groups.find((g) => String(g.id) === String(data.Item_Group));
    if (!grp) return;

    const grpCode = (grp.Group_Code ?? grp.code ?? "")
      .toString()
      .trim();
    if (!grpCode) return;

    const prefix = `${grpCode}-`;
    let cancelled = false;

    (async () => {
      try {
        const rows = await getDataWithComp(
          "Tb_Set_Item",
          `AND Item_Code_Master LIKE ${q(`${prefix}%`)} ORDER BY Item_Code_Master DESC`
        );
        const list = Array.isArray(rows) ? rows : rows?.data || [];
        const latest = list[0]?.Item_Code_Master || "";
        let nextNum = 1;
        if (String(latest).startsWith(prefix)) {
          const tail = String(latest).slice(prefix.length);
          const num = parseInt(tail, 10);
          if (!Number.isNaN(num)) nextNum = num + 1;
        }
        const nextCode = `${prefix}${String(nextNum).padStart(4, "0")}`;
        if (!cancelled) setItemBaseCode(nextCode);
      } catch (e) {
        console.error("load latest Item_Code_Master error:", e);
        if (!cancelled) setItemBaseCode(`${prefix}0001`);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, mode, data.Item_Group, groups]);

  /* =========================================================
   * 6) (CREATE) auto WH จาก localStorage (WHID)
   * =======================================================*/
  const whIdsInitializedRef = useRef(false);
  
  useEffect(() => {
    if (!isOpen) {
      // Reset flag เมื่อปิด modal
      whIdsInitializedRef.current = false;
      return;
    }
    if (mode !== "create") return;
    if (!warehouses.length) return;

    // ✅ Auto-select เพียงครั้งเดียวเมื่อเปิด modal (ไม่ทับการเลือกของผู้ใช้)
    if (whIdsInitializedRef.current) return;
    if (whIds.length > 0) {
      // ถ้าผู้ใช้เลือกคลังเองแล้ว → ตั้ง flag และไม่ auto ทับ
      whIdsInitializedRef.current = true;
      return;
    }

    const localWHID =
      localStorage.getItem("WHID") ||
      localStorage.getItem("whId") ||
      localStorage.getItem("WHId") ||
      "";

    const found = warehouses.find((w) => String(w.id) === String(localWHID));

    if (found) {
      setWhIds([found.id]);
    } else {
      // ✅ fallback: ไม่เลือกคลังใดๆ (ให้ผู้ใช้เลือกเอง)
      // setWhIds(warehouses.map((w) => w.id));
    }
    
    // ตั้ง flag ว่าได้ auto-select แล้ว
    whIdsInitializedRef.current = true;
  }, [isOpen, mode, warehouses]);

  /* =========================================================
   * 7) (CREATE) preview rows = Cartesian สี × ไซส์
   *    ✅ รองรับกรณีไม่เลือกสี/  (สร้างแถวเดียว)
   *    ✅ แก้ไข: ป้องกันการต่อชื่อสี/ไซส์ซ้ำ (case-insensitive)
   * =======================================================*/
  useEffect(() => {
    if (mode === "edit") return;
     if (!colours.length && !sizes.length && !units.length) return;

    const baseShort = itemBaseCode ? itemBaseCode.toString().trim() : "";

    if (!baseShort) {
      setRows((prev) => prev.filter((r) => r.__manual && !r.__deleted));
      setSelectedRowKeys([]);
      return;
    }

    const unitName =
      units.find((u) => String(u.name) === String(data.Item_Unit_IN))?.name ||
      data.Item_Unit_IN ||
      "";

    const colourSet = new Set((selColourIds || []).map((x) => String(x)));
    const sizeSet = new Set((selSizeIds || []).map((x) => String(x)));

    console.debug("🟢 [CREATE] Cartesian product");
    console.debug("  selColourIds:", selColourIds);
    console.debug("  selSizeIds:", selSizeIds);
    console.debug("  colourSet:", Array.from(colourSet));
    console.debug("  sizeSet:", Array.from(sizeSet));

    const cs = selColourIds.length
      ? colours.filter((c) => colourSet.has(String(c.id)))
      : [{ id: "", name: "", code: "" }];

    const ss = selSizeIds.length
      ? sizes.filter((s) => sizeSet.has(String(s.id)))
      : [{ id: "", name: "", code: "" }];

    console.debug(
      "  cs (selected colours):",
      cs.map((c) => `${c.name} (ID:${c.id})`)
    );
    console.debug(
      "  ss (selected sizes):",
      ss.map((s) => `${s.name} (ID:${s.id})`)
    );

    const whText = selectedWarehouseNames.join(", ");
    const init = Number(initQty || 0); // ✅ เก็บค่านี้ไว้

    const out = [];
    let idx = 0;

    for (const c of cs) {
      for (const s of ss) {
        const colorCode = c.code || "";
        const colorName = c.name || "";
        const sizeCode = s.code || "";
        const sizeName = s.name || "";

        const code = `${baseShort}${colorCode}${sizeCode}`;
        const statusYN = data.Item_Status || "Y";

        const baseName = (data.Item_Name || "").trim();

        // ✅ สร้างชื่อเต็มจาก Item_Name + สี + ไซส์ โดยตรง
        const parts = [baseName];
        if (colorName) parts.push(colorName);
        if (sizeName) parts.push(sizeName);
        const fullItemName = parts.join(" ").trim();

        out.push({
          key: `${code}-${idx++}`,
          Item_Id: null,
          __isNew: true,
          Item_Barcode: code,
          Item_Code: code,
          Colour: colorName,
          Size: sizeName,
          Size_Name: sizeName,
          Item_Name: fullItemName,
          Item_Name_EN: data.Item_Name_EN || "",
          Item_Unit_IN: unitName,
          Item_Qty: Number(data.Item_Qty || 0),
          Item_Is_Sale: data.Item_Is_Sale || "N",
          Item_Is_Service: data.Item_Is_Service || "N",
          Brand: data.Item_Brand ?? "",
          Series: data.Item_Series ?? "",
          Cost: Number(data.Item_Cost ?? 0),
          Price1: Number(data.Item_Price ?? 0),
          Price2: Number(data.Item_PriceA ?? 0),
          Price3: Number(data.Item_PriceB ?? 0),
          StatusYN: statusYN,
          Status: statusYN === "Y" ? "Active" : "In-Active",
          InitQty: init, // ✅ เปลี่ยนจาก Init_Qty เป็น InitQty
          WH_Text: whText,
          __deleted: false,
        });
      }
    }

    setRows((prev) => {
      const manualRows = prev.filter((r) => r.__manual && !r.__deleted);
      return manualRows.length ? [...out, ...manualRows] : out;
    });
    setSelectedRowKeys([]);
  }, [
    mode,
    groups,
    units,
    colours,
    sizes,
    data.Item_Group,
    itemBaseCode,
    data.Item_Unit_IN,
    data.Item_Name,
    data.Item_Name_EN,
    data.Item_Brand,
    data.Item_Series,
    data.Item_Cost,
    data.Item_Price,
    data.Item_PriceA,
    data.Item_PriceB,
    data.Item_Status,
    data.Item_Qty,
    data.Item_Is_Sale,
    data.Item_Is_Service,
    selColourIds,
    selSizeIds,
    selectedWarehouseNames,
    initQty, // ✅ dependency สำคัญ!
  ]);

  /* =========================================================
   * 8) (EDIT) add variants เมื่อเลือกสี/ไซส์เพิ่ม (ไม่ทับของเดิม)
   *    ✅ FIX: เช็คซ้ำกับ DB ก่อนสร้างแถวใหม่
   * =======================================================*/
  useEffect(() => {
    if (!isOpen) return;
    if (mode !== "edit") return;

    const g = groups.find((x) => String(x.id) === String(data.Item_Group));
    const groupCode = (g?.code || "").toString().trim();

    // ✅ ใช้ Item_Code_Master จาก DB (เก็บไว้ตอนสร้าง)
    // หาแถวแรกที่ไม่ถูกลบ
    const firstRow = rows.find((r) => !r.__deleted);
    let baseShort; // ✅ Declare baseShort variable

    if (firstRow?.Item_Code_Master) {
      // ✅ กรณีมี Item_Code_Master → ใช้เลย (ถูกต้อง 100%)
      baseShort = String(firstRow.Item_Code_Master).trim();
    } else if (firstRow?.Item_Code) {
      // ⚠️ Fallback: ใช้ Item_Code โดยตรงเป็น base code (ไม่ตัดสี/ไซส์ออก)
      // สำหรับข้อมูลเก่าที่ยังไม่มี Item_Code_Master
      baseShort = String(firstRow.Item_Code).trim();
    }

    // fallback ถ้าหาไม่เจอ
    if (!baseShort) {
      baseShort = data.Item_Short_Name
        ? data.Item_Short_Name.toString().trim()
        : groupCode;
    }

    if (!baseShort) return;

    console.debug("🔵 [EDIT] useEffect add variants triggered");
    console.debug("  selColourIds:", selColourIds);
    console.debug("  selSizeIds:", selSizeIds);
    console.debug("  current rows count:", rows.length);

    // ✅ ถ้ามี rows อยู่แล้ว เช็คว่ามีการเพิ่มสี/ไซส์ใหม่หรือไม่
    if (rows.length > 0) {
      const existingColours = new Set(
        rows
          .filter((r) => !r.__deleted)
          .map((r) => {
            const colourVal = r.Colour || r.Item_Colour || "";
            return colours.find(
              (c) => c.name === colourVal || c.code === colourVal
            )?.id;
          })
          .filter((id) => id != null)
          .map(String)
      );

      const existingSizes = new Set(
        rows
          .filter((r) => !r.__deleted)
          .map((r) => {
            const sizeVal = r.Size_Name || r.Size || r.Item_Size || "";
            return sizes.find((s) => s.name === sizeVal || s.code === sizeVal)
              ?.id;
          })
          .filter((id) => id != null)
          .map(String)
      );

      const selectedColours = new Set((selColourIds || []).map(String));
      const selectedSizes = new Set((selSizeIds || []).map(String));

      // ถ้า selColourIds และ selSizeIds ไม่มีอะไรเพิ่มจาก existing → skip
      const hasNewColour = [...selectedColours].some(
        (id) => !existingColours.has(id)
      );
      const hasNewSize = [...selectedSizes].some(
        (id) => !existingSizes.has(id)
      );

      console.debug("  existing colours in rows:", Array.from(existingColours));
      console.debug("  existing sizes in rows:", Array.from(existingSizes));
      console.debug("  hasNewColour:", hasNewColour);
      console.debug("  hasNewSize:", hasNewSize);

      if (!hasNewColour && !hasNewSize) {
        console.debug("  ➡️ ไม่มีสี/ไซส์ใหม่ → skip (ไม่สร้าง variants)");
        return;
      }
    }
    console.debug("  current rows count:", rows.length);

    // --- helpers ---
    const normalize = (s = "") =>
      String(s).trim().toLowerCase().replace(/\s+/g, " ");

    const variantKey = (baseName, colourName, sizeCode) =>
      `${normalize(baseName)}|${normalize(colourName)}|${normalize(sizeCode)}`;

    // normalize id เป็น string ก่อนกรอง
    const colourSet = new Set((selColourIds || []).map((x) => String(x)));
    const sizeSet = new Set((selSizeIds || []).map((x) => String(x)));

    const cs = selColourIds.length
      ? colours.filter((c) => colourSet.has(String(c.id)))
      : [{ id: "", name: "", code: "" }];

    const ss = selSizeIds.length
      ? sizes.filter((s) => sizeSet.has(String(s.id)))
      : [{ id: "", name: "", code: "" }];

    console.debug(
      "  cs (selected colours):",
      cs.map((c) => `${c.name} (ID:${c.id})`)
    );
    console.debug(
      "  ss (selected sizes):",
      ss.map((s) => `${s.name} (ID:${s.id})`)
    );

    if (selColourIds.length > 0 && cs.length === 0) {
      console.debug(
        "  ⚠️ selColourIds มีค่า แต่ filter ไม่เจอสีใดในระบบ → skip"
      );
      return;
    }
    if (selSizeIds.length > 0 && ss.length === 0) {
      console.debug(
        "  ⚠️ selSizeIds มีค่า แต่ filter ไม่เจอไซส์ใดในระบบ → skip"
      );
      return;
    }

    const whText = warehouses
      .filter((w) => whIds.map(String).includes(String(w.id)))
      .map((x) => x.name)
      .join(", ");

    // ✅ ใช้ data.Item_Name ใน create mode, initialData?.header?.Item_Short_Name2 ใน edit mode
    const baseName = mode === "create" 
      ? (data.Item_Name || "")
      : (initialData?.header?.Item_Short_Name2 || "");

    (async () => {
      const newVariants = [];

      // ✅ helper function สำหรับ normalize text
      const normalizeText = (text) =>
        String(text || "")
          .toLowerCase()
          .replace(/\s+/g, " ")
          .trim();

      for (const c of cs) {
        for (const s of ss) {
          const code = `${baseShort}${c.code || ""}${s.code || ""}`;
          const colorName = c.name || "";
          const sizeName = s.name || "";

          // ✅ ตรวจสอบว่าชื่อมีสี/ไซส์อยู่แล้วหรือไม่
          const baseNameLower = normalizeText(baseName);
          const hasColour = colorName && baseNameLower.includes(normalizeText(colorName));
          const hasSize = sizeName && baseNameLower.includes(normalizeText(sizeName));

          let fullName = baseName;
          if (colorName && !hasColour) fullName += ` ${colorName}`;
          if (sizeName && !hasSize) fullName += ` ${sizeName}`;
          fullName = fullName.trim();

          newVariants.push({
            code,
            fullName,
            colour: c,
            size: s,
          });
        }
      }

      // ✅ เช็คซ้ำกับ DB แบบ batch
      if (newVariants.length === 0) {
        console.debug("  ⚠️ newVariants is empty, skip");
        return;
      }

      // console.debug("  newVariants count:", newVariants.length);
      // console.debug(
      //   "  newVariants:",
      //   newVariants.map((v) => `${v.code} (${v.colour.name}/${v.size.name})`)
      // );

      // const compId = window.localStorage.getItem("company");
      const codes = newVariants.map((v) => v.code);
      const names = newVariants.map((v) => v.fullName);

      const q = (s) => `'${String(s ?? "").replaceAll("'", "''")}'`;

      const duplicateCheck = await getDataWithComp(
        "Tb_Set_Item",
        `AND (Item_Code IN (${codes.map(q).join(",")}) OR Item_Name IN (${names
          .map(q)
          .join(",")}))`
      );

      const existingCodes = new Set(
        (duplicateCheck || []).map((r) => String(r.Item_Code || ""))
      );
      const existingNames = new Set(
        (duplicateCheck || []).map((r) => normalize(r.Item_Name || ""))
      );

      setRows((prev) => {
        const existingRowCodes = new Set(
          prev
            .filter((r) => !r.__deleted)
            .map((r) => String(r.Item_Code || r.Item_Barcode || ""))
        );

        const existingRowKeys = new Set(
          prev
            .filter((r) => !r.__deleted)
            .map((r) =>
              variantKey(
                baseName,
                r.Colour || r.Item_Colour || "",
                r.Size_Name || r.Size || r.Item_Size || ""
              )
            )
        );

        // console.debug("  existing row codes:", Array.from(existingRowCodes));
        // console.debug("  existing row keys:", Array.from(existingRowKeys));

        const toAdd = [];
        let idx = prev.length;

        for (const variant of newVariants) {
          const vKey = variantKey(
            baseName,
            variant.colour.name || "",
            variant.size.name || ""
          );

          const isDupCode = existingCodes.has(variant.code);
          const isDupName = existingNames.has(normalize(variant.fullName));
          const isDupRowCode = existingRowCodes.has(variant.code);
          const isDupRowKey = existingRowKeys.has(vKey);

          // console.debug(
          //   `    checking: ${variant.code} (${variant.colour.name}/${variant.size.name})`
          // );
          // console.debug(`      vKey: "${vKey}"`);
          // console.debug(
          //   `      dup? DB-code:${isDupCode} DB-name:${isDupName} row-code:${isDupRowCode} row-key:${isDupRowKey}`
          // );

          if (isDupCode || isDupName || isDupRowCode || isDupRowKey) {
            // console.debug(`      → SKIP (duplicate)`);
            continue; // ข้ามไม่สร้าง
          }

          console.debug(`      → ADD`);

          // ✅ ไม่ซ้ำ สร้างแถวใหม่
          toAdd.push(
            buildVariantRow({
              baseShort,
              colour: variant.colour,
              size: variant.size,
              idx: idx++,
              whText,
            })
          );

          existingRowCodes.add(variant.code);
          existingRowKeys.add(vKey);
        }

        console.debug("  ✅ toAdd count:", toAdd.length);

        return toAdd.length ? [...prev, ...toAdd] : prev;
      });
    })();
  }, [
    isOpen,
    mode,
    data.Item_Group,
    data.Item_Short_Name,
    data.Item_Name,
    selColourIds,
    selSizeIds,
    groups,
    colours,
    sizes,
    whIds,
    warehouses,
  ]);

  /* =========================================================
   * 8.5) ลบแถวเมื่อยกเลิกการเลือกสี/ไซส์
   * =======================================================*/
  useEffect(() => {
    if (!isOpen) return;
    if (mode === "edit") return; // ✅ เพิ่มบรรทัดนี้กันลบตอนโหมดแก้ไข

    setRows((prev) => {
      return prev.map((r) => {
        if (r.__deleted) return r;

        const rowColour = r.Colour || r.Item_Colour || "";
        const rowSize = r.Size || r.Item_Size || "";

        const rowColourId = colours.find(
          (c) => c.name === rowColour || c.code === rowColour
        )?.id;
        const rowSizeId = sizes.find(
          (s) => s.name === rowSize || s.code === rowSize
        )?.id;

        if (rowColour && rowColourId && !selColourIds.includes(rowColourId)) {
          return { ...r, __deleted: true };
        }
        if (rowSize && rowSizeId && !selSizeIds.includes(rowSizeId)) {
          return { ...r, __deleted: true };
        }
        return r;
      });
    });
  }, [isOpen, mode, selColourIds, selSizeIds, colours, sizes]);

  /* =========================================================
   * 9) (EDIT) sync header fields -> rows (ที่ไม่ถูกลบ)
   *    ✅ FIX: อย่าทับ Item_Name (ทำให้ชื่อเต็มหาย/กันซ้ำพัง)
   *    ✅ อัปเดต Item_Code เมื่อ itemBaseCode เปลี่ยน
   * =======================================================*/
  useEffect(() => {
    if (!isOpen || mode !== "edit") return;

    setRows((prev) =>
      prev.map((r) => {
        if (r.__deleted) return r;

        // ✅ สร้างรหัสใหม่จาก itemBaseCode + color + size
        const colorCode =
          colours.find((c) => c.name === (r.Colour || r.Item_Colour || ""))
            ?.code || "";

        const sizeCode =
          sizes.find(
            (s) =>
              s.name === (r.Size_Name || r.Size || r.Item_Size || "") ||
              s.code === (r.Size || r.Item_Size || "")
          )?.code || "";

        const newCode = itemBaseCode
          ? `${itemBaseCode}${colorCode}${sizeCode}`
          : r.Item_Code;

        return {
          ...r,
          Item_Code: newCode,
          Item_Barcode: newCode,
          // ✅ ไม่ทับ Item_Name_EN ของแถวใหม่ (Item_Id === null)
          Item_Name_EN: r.Item_Id === null ? r.Item_Name_EN : data.Item_Name_EN,
          Item_Unit_IN: data.Item_Unit_IN,
          Cost: Number(data.Item_Cost ?? 0),
          // Price1/2/3 ไม่ sync อัตโนมัติ (คุณใช้ modal ยืนยันอยู่แล้ว)
          // Brand/Series/Status ยัง sync ได้ตามเดิม
          Brand: data.Item_Brand ?? "",
          Series: data.Item_Series ?? "",
          StatusYN: data.Item_Status ?? "Y",
        };
      })
    );
  }, [
    isOpen,
    mode,
    itemBaseCode, // ✅ เพิ่ม dependency
    data.Item_Name_EN,
    data.Item_Unit_IN,
    data.Item_Cost,
    data.Item_Brand,
    data.Item_Series,
    data.Item_Status,
    colours, // ✅ เพิ่มเพื่อหา color code
    sizes, // ✅ เพิ่มเพื่อหา size code
  ]);

  useEffect(() => {
    if (!isOpen || mode !== "edit") return;

    const base = String(data.Item_Name ?? "").trim();
    setRows((prev) =>
      prev.map((r) => {
        if (r.__deleted || r.__isNew) return r;

        const colour = r.Colour ?? r.Item_Colour ?? "";
        const size = r.Size ?? r.Item_Size ?? "";
        const parts = [base];
        if (colour) parts.push(colour);
        if (size) parts.push(size);

        return { ...r, Item_Name: parts.join(" ").trim() };
      })
    );
  }, [isOpen, mode, data.Item_Name]);

  if (!isOpen) return null;

  return createPortal(
    <div className="pm-overlay" role="dialog" aria-modal="true">
      <Spin
        spinning={isMasterLoading}
        // tip={isMasterLoading ? "กำลังโหลดข้อมูล..." : "กำลังดำเนินการ..."}
        fullscreen
      />
      <div className="pm-modal">
        {/* HEADER */}
        <div className="pm-header">
          <div className="pm-title">
            {mode === "create" ? "สร้างสินค้าใหม่" : "แก้ไขสินค้า"}
          </div>
          <button className="pm-close" onClick={onClose} aria-label="close">
            ×
          </button>
        </div>

        {/* BODY */}
          <div className="pm-body">
            <div className="pm-form pm-form--create">
              {mode === "create" && (
                <div style={{ display: "flex", gap: 12, width: "100%" }}>
                  <div className="pm-col" style={{ flex: 1 }}>
                    <label
                      className="label-modal"
                      style={{
                        width: "90px",
                        display: "flex",
                        justifyContent: "end",
                      }}
                    >
                      หมวดหมู่สินค้า <span style={{ color: "red" }}>*</span>
                    </label>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        width: "100%",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <SmartSelect
                          className="w-100"
                          style={{ width: "100%" }}
                          allowClear
                          placeholder="เลือกหมวดหมู่"
                          value={String(data.Item_Group || "")}
                          onChange={(v) =>
                            setData((s) => ({
                              ...s,
                              Item_Group:
                                typeof v === "object"
                                  ? v?.value != null
                                    ? String(v.value)
                                    : ""
                                  : v != null
                                  ? String(v)
                                  : "",
                            }))
                          }
                          options={[...groups]
                            .sort((a, b) =>
                              a.name.localeCompare(b.name, "th", {
                                numeric: true,
                                sensitivity: "base",
                              })
                            )
                            .map((g) => ({
                              label: g.name,
                              value: String(g.id),
                            }))}
                          maxRender={120}
                          debounceMs={180}
                          getPopupContainer={(trigger) => trigger.parentElement}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowAddCategoryUnitModal(true);
                        }}
                        style={{
                          padding: "0 12px",
                          height: "34px",
                          borderRadius: 6,
                          background: "#28a745",
                          color: "white",
                          border: "none",
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 4,
                          transition: "all 0.2s",
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#218838";
                          e.currentTarget.style.transform = "scale(1.05)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#28a745";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                        title="เพิ่มหมวดหมู่ใหม่"
                      >
                        <PlusOutlined />
                      </button>
                    </div>
                  </div>

                  <div className="pm-col" style={{ flex: 1 }}>
                    <label
                      className="label-modal"
                      style={{
                        width: "90px",
                        display: "flex",
                        justifyContent: "end",
                      }}
                    >
                      รหัสสินค้า<span style={{ color: "red" }}>*</span>
                    </label>
                    <input
                      name="itemBaseCode"
                      value={itemBaseCode}
                      onChange={(e) => {
                        baseCodeDirtyRef.current = true;
                        setItemBaseCode(e.target.value);
                      }}
                      disabled={mode === "edit"}
                      placeholder="รหัสสินค้าตั้งต้น"
                      style={{ width: "100%" }}
                    />
                  </div>

                  <div className="pm-col" style={{ flex: 1 }}>
                    <label
                      className="label-modal"
                      style={{
                        width: "90px",
                        display: "flex",
                        justifyContent: "end",
                      }}
                    >
                      คลัง <span style={{ color: "red" }}>*</span>
                    </label>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Select
                          allowClear
                          value={whIds[0] || undefined}
                          onChange={(val) => setWhIds(val ? [val] : [])}
                          placeholder="เลือกคลัง"
                          className="pm-ant-select"
                          style={{ width: "100%" }}
                          options={warehouses.map((w) => ({
                            value: w.id,
                            label: w.name,
                          }))}
                          disabled={mode === "edit"}
                          getPopupContainer={(trigger) =>
                            trigger.parentElement
                          }
                        />
                      </div>
                      {mode !== "edit" && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAddWarehouseModal(true);
                          }}
                          style={{
                            padding: "0 12px",
                            height: "34px",
                            borderRadius: 6,
                            background: "#28a745",
                            color: "white",
                            border: "none",
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 4,
                            transition: "all 0.2s",
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#218838";
                            e.currentTarget.style.transform = "scale(1.05)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#28a745";
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          title="เพิ่มคลังใหม่"
                        >
                          <PlusOutlined />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {mode !== "create" && compactEditHeader && (
                <div style={{ display: "flex", gap: 12, width: "100%" }}>
                  <div className="pm-col" style={{ flex: 1 }}>
                    <label
                      className="label-modal"
                      style={{
                        width: "90px",
                        display: "flex",
                        justifyContent: "end",
                      }}
                    >
                      หมวดหมู่สินค้า <span style={{ color: "red" }}>*</span>
                    </label>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        width: "100%",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <SmartSelect
                          className="w-100"
                          style={{ width: "100%" }}
                          allowClear
                          placeholder="เลือกหมวดหมู่"
                          value={String(data.Item_Group || "")}
                          onChange={(v) =>
                            setData((s) => ({
                              ...s,
                              Item_Group:
                                typeof v === "object"
                                  ? v?.value != null
                                    ? String(v.value)
                                    : ""
                                  : v != null
                                  ? String(v)
                                  : "",
                            }))
                          }
                          options={[...groups]
                            .sort((a, b) =>
                              a.name.localeCompare(b.name, "th", {
                                numeric: true,
                                sensitivity: "base",
                              })
                            )
                            .map((g) => ({
                              label: g.name,
                              value: String(g.id),
                            }))}
                          maxRender={120}
                          debounceMs={180}
                          getPopupContainer={(trigger) => trigger.parentElement}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowAddCategoryUnitModal(true);
                        }}
                        style={{
                          padding: "0 12px",
                          height: "34px",
                          borderRadius: 6,
                          background: "#28a745",
                          color: "white",
                          border: "none",
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 4,
                          transition: "all 0.2s",
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#218838";
                          e.currentTarget.style.transform = "scale(1.05)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#28a745";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                        title="เพิ่มหมวดหมู่ใหม่"
                      >
                        <PlusOutlined />
                      </button>
                    </div>
                  </div>

                  <div className="pm-col" style={{ flex: 1 }}>
                    <label
                      className="label-modal"
                      style={{
                        width: "90px",
                        display: "flex",
                        justifyContent: "end",
                      }}
                    >
                      คลัง <span style={{ color: "red" }}>*</span>
                    </label>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Select
                          allowClear
                          value={whIds[0] || undefined}
                          onChange={(val) => setWhIds(val ? [val] : [])}
                          placeholder="เลือกคลัง"
                          className="pm-ant-select"
                          style={{ width: "100%" }}
                          options={warehouses.map((w) => ({
                            value: w.id,
                            label: w.name,
                          }))}
                          disabled={mode === "edit"}
                          getPopupContainer={(trigger) => trigger.parentElement}
                        />
                      </div>
                      {mode !== "edit" && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAddWarehouseModal(true);
                          }}
                          style={{
                            padding: "0 12px",
                            height: "34px",
                            borderRadius: 6,
                            background: "#28a745",
                            color: "white",
                            border: "none",
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 4,
                            transition: "all 0.2s",
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#218838";
                            e.currentTarget.style.transform = "scale(1.05)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#28a745";
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          title="เพิ่มคลังใหม่"
                        >
                          <PlusOutlined />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {mode !== "create" && !compactEditHeader && (
                <>
                  {/* ============ LEFT (หมวด/หน่วย/รหัส + ชื่อไทย/ยี่ห้อ/รุ่น) ============ */}
                  <div className="pm-left">
              {/* ซ้ายย่อย: หมวด/หน่วย/รหัส */}
              <div className="pm-left-col">
                <div className="pm-col">
                  <label
                    className="label-modal"
                    style={{
                      width: "90px",
                      display: "flex",
                      justifyContent: "end",
                    }}
                  >
                    หมวดหมู่สินค้า <span style={{ color: "red" }}>*</span>
                  </label>
                  <div style={{ display: "flex", gap: "8px", width: "100%", alignItems: "center" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <SmartSelect
                        className="w-100"
                        style={{ width: "100%" }}
                        allowClear
                        placeholder="เลือกหมวดหมู่"
                        value={String(data.Item_Group || "")}
                        onChange={(v) =>
                          setData((s) => ({
                            ...s,
                            Item_Group:
                              typeof v === "object"
                                ? v?.value != null
                                  ? String(v.value)
                                  : ""
                                : v != null
                                ? String(v)
                                : "",
                          }))
                        }
                        options={[...groups]
                          .sort((a, b) =>
                            a.name.localeCompare(b.name, "th", {
                              numeric: true,
                              sensitivity: "base",
                            })
                          )
                          .map((g) => ({
                            label: g.name,
                            value: String(g.id),
                          }))}
                        maxRender={120}
                        debounceMs={180}
                        getPopupContainer={(trigger) => trigger.parentElement}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowAddCategoryUnitModal(true);
                      }}
                      style={{
                        padding: "0 12px",
                        height: "34px",
                        borderRadius: 6,
                        background: "#28a745",
                        color: "white",
                        border: "none",
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 4,
                        transition: "all 0.2s",
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#218838";
                        e.currentTarget.style.transform = "scale(1.05)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "#28a745";
                        e.currentTarget.style.transform = "scale(1)";
                      }}
                      title="เพิ่มหมวดหมู่ใหม่"
                    >
                      <PlusOutlined />
                    </button>
                  </div>
                </div>

                {mode !== "create" && (
                  <div
                    style={{
                      display: "flex",
                      gap: 12,
                      width: "100%",
                    }}
                  >
                    <div className="pm-col" style={{ flex: 1 }}>
                      <label
                        className="label-modal"
                        style={{
                          width: "90px",
                          display: "flex",
                          justifyContent: "end",
                        }}
                      >
                        ปริมาณ
                      </label>
                      <InputNumber
                        size="small"
                        style={{ width: "100%" }}
                        inputStyle={{ textAlign: "right" }}
                        min={0}
                        value={data.Item_Qty}
                        controls={false}
                        onChange={(v) =>
                          setData((s) => ({
                            ...s,
                            Item_Qty: v ?? "",
                          }))
                        }
                      />
                    </div>
                    <div className="pm-col" style={{ flex: 1 }}>
                      <label
                        className="label-modal"
                        style={{
                          width: "90px",
                          display: "flex",
                          justifyContent: "end",
                        }}
                      >
                        หน่วย<span style={{ color: "red" }}>*</span>
                      </label>
                      <div style={{ display: "flex", gap: 8, width: "100%" }}>
                        <SmartSelect
                          className="w-100"
                          allowClear
                          placeholder="เลือกหน่วยนับ"
                          value={data.Item_Unit_IN}
                          onChange={(v) => {
                            unitDirtyRef.current = true;
                            setData((s) => ({
                              ...s,
                              Item_Unit_IN: v?.value ?? v?.label ?? "",
                            }));
                          }}
                          options={units
                            .sort((a, b) => a.name.localeCompare(b.name, "th"))
                            .map((u) => ({
                              label: u.name,
                              value: u.name,
                              id: u.id,
                            }))}
                          maxRender={120}
                          debounceMs={180}
                          returnObject
                        />
                        <button
                          type="button"
                          className="btn btn-sm"
                          title="เพิ่มหน่วยใหม่"
                          onClick={() => {
                            setNewUnitName("");
                            setShowAddUnitModal(true);
                          }}
                          style={{
                            minWidth: 36,
                            height: 36,
                            borderRadius: 6,
                            backgroundColor: "#28a745",
                            color: "white",
                            border: "none",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <PlusOutlined />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {mode !== "create" && (
                  <div className="pm-col">
                    <label
                      className="label-modal"
                      style={{
                        width: "90px",
                        display: "flex",
                        justifyContent: "end",
                      }}
                    >
                      การใช้งาน
                    </label>
                    <div
                      style={{ display: "flex", gap: 16, alignItems: "center" }}
                    >
                      <Checkbox
                        checked={String(data.Item_Is_Sale) === "Y"}
                        onChange={(e) =>
                          setData((s) => ({
                            ...s,
                            Item_Is_Sale: e.target.checked ? "Y" : "N",
                          }))
                        }
                      >
                        สำหรับจำหน่าย
                      </Checkbox>
                      <Checkbox
                        checked={String(data.Item_Is_Service) === "Y"}
                        onChange={(e) =>
                          setData((s) => ({
                            ...s,
                            Item_Is_Service: e.target.checked ? "Y" : "N",
                          }))
                        }
                      >
                        สำหรับใช้ในบริการ
                      </Checkbox>
                    </div>
                  </div>
                )}
              </div>

              {/* ขวาย่อย: ชื่อไทย/ยี่ห้อ/รุ่น */}
              {mode !== "create" && (
                <div className="pm-left-col">
                  <div className="pm-col">
                    <label
                      className="label-modal"
                      style={{
                        width: "90px",
                        display: "flex",
                        justifyContent: "end",
                      }}
                    >
                      ชื่อสินค้า (ไทย) <span style={{ color: "red" }}>*</span>
                    </label>
                    <input
                      name="Item_Name"
                      value={data.Item_Name}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="pm-col">
                    <label
                      className="label-modal"
                      style={{
                        width: "90px",
                        display: "flex",
                        justifyContent: "end",
                      }}
                    >
                      ยี่ห้อ
                    </label>
                    <input
                      name="Item_Brand"
                      value={data.Item_Brand}
                      onChange={handleChange}
                      placeholder="ยี่ห้อ"
                    />
                  </div>

                  <div className="pm-col">
                    <label
                      className="label-modal"
                      style={{
                        width: "90px",
                        display: "flex",
                        justifyContent: "end",
                      }}
                    >
                      รุ่น
                    </label>
                    <input
                      name="Item_Series"
                      value={data.Item_Series}
                      onChange={handleChange}
                      placeholder="รุ่น"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* ============ RIGHT SIDE (คลัง + ราคา) ============ */}
            <div className="pm-right-wrapper">
              {/* คลัง / จำนวนตั้งต้น */}
              <div className="pm-stock">
                <div className="pm-col">
                  <label
                    className="label-modal"
                    style={{
                      width: "90px",
                      display: "flex",
                      justifyContent: "end",
                    }}
                  >
                    คลัง <span style={{ color: "red" }}>*</span>
                  </label>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center", width: "100%" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Select
                        allowClear
                        value={whIds[0] || undefined}
                        onChange={(val) => setWhIds(val ? [val] : [])}
                        placeholder="เลือกคลัง"
                        className="pm-ant-select"
                        style={{ width: "100%" }}
                        options={warehouses.map((w) => ({
                          value: w.id,
                          label: w.name,
                        }))}
                        disabled={mode === "edit"}
                        getPopupContainer={(trigger) => trigger.parentElement}
                      />
                    </div>
                    {mode !== "edit" && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowAddWarehouseModal(true);
                        }}
                        style={{
                          padding: "0 12px",
                          height: "34px",
                          borderRadius: 6,
                          background: "#28a745",
                          color: "white",
                          border: "none",
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 4,
                          transition: "all 0.2s",
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#218838";
                          e.currentTarget.style.transform = "scale(1.05)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#28a745";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                        title="เพิ่มคลังใหม่"
                      >
                        <PlusOutlined />
                      </button>
                    )}
                  </div>
                </div>

                {mode !== "create" && (
                  <div className="pm-col">
                    <label
                      className="label-modal"
                      style={{
                        width: "90px",
                        display: "flex",
                        justifyContent: "end",
                      }}
                    >
                      จำนวนตั้งต้น
                    </label>
                    <InputNumber
                      value={initQty === "" ? null : Number(initQty)}
                      onChange={(v) => setInitQty(v ?? "")}
                      formatter={formatterWithCommas}
                      parser={parserStrict}
                      precision={2}
                      inputMode="decimal"
                      onKeyDown={onlyNumberKeyDown}
                      onFocus={selectOnFocus}
                      style={{ width: "100%" }}
                      disabled={mode === "edit"}
                    />
                  </div>
                )}
              </div>

              {/* ต้นทุน / ราคา 1-3 */}
              {mode !== "create" && (
                <div className="pm-prices">
                <div className="pm-col">
                  <label
                    className="label-modal"
                    style={{
                      width: "90px",
                      display: "flex",
                      justifyContent: "end",
                    }}
                  >
                    ต้นทุน
                  </label>
                  <InputNumber
                    value={
                      data.Item_Cost === "" ? null : Number(data.Item_Cost)
                    }
                    onChange={(v) =>
                      setData((s) => ({ ...s, Item_Cost: v ?? "" }))
                    }
                    formatter={formatterWithCommas}
                    parser={parserStrict}
                    precision={2}
                    inputMode="decimal"
                    onKeyDown={onlyNumberKeyDown}
                    onFocus={selectOnFocus}
                    style={{ width: "100%" }}
                  />
                </div>

                <div className="pm-col">
                  <label
                    className="label-modal"
                    style={{
                      width: "90px",
                      display: "flex",
                      justifyContent: "end",
                    }}
                  >
                    ราคา 1
                  </label>
                  <InputNumber
                    value={
                      data.Item_Price === "" ? null : Number(data.Item_Price)
                    }
                    onChange={(v) =>
                      setData((s) => ({ ...s, Item_Price: v ?? "" }))
                    }
                    onFocus={(e) => {
                      headerPriceBeforeChangeRef.current.Item_Price = Number(
                        data.Item_Price ?? 0
                      );
                      rowPricesBeforeChangeRef.current = rows
                        .filter((r) => !r.__deleted)
                        .map((r) => ({ key: r.key, value: r.Price1 ?? 0 }));
                      selectOnFocus(e);
                    }}
                    onBlur={(e) => {
                      const inputValue = e.target.value;
                      const newValue = inputValue
                        ? Number(inputValue.replace(/,/g, ""))
                        : 0;
                      handlePriceBlur("Item_Price", newValue);
                    }}
                    formatter={formatterWithCommas}
                    parser={parserStrict}
                    precision={2}
                    inputMode="decimal"
                    onKeyDown={onlyNumberKeyDown}
                    style={{ width: "100%" }}
                  />
                </div>

                <div className="pm-col">
                  <label
                    className="label-modal"
                    style={{
                      width: "90px",
                      display: "flex",
                      justifyContent: "end",
                    }}
                  >
                    ราคา 2
                  </label>
                  <InputNumber
                    value={
                      data.Item_PriceA === "" ? null : Number(data.Item_PriceA)
                    }
                    onChange={(v) =>
                      setData((s) => ({ ...s, Item_PriceA: v ?? "" }))
                    }
                    onFocus={(e) => {
                      headerPriceBeforeChangeRef.current.Item_PriceA = Number(
                        data.Item_PriceA ?? 0
                      );
                      rowPricesBeforeChangeRef.current = rows
                        .filter((r) => !r.__deleted)
                        .map((r) => ({ key: r.key, value: r.Price2 ?? 0 }));
                      selectOnFocus(e);
                    }}
                    onBlur={(e) => {
                      const inputValue = e.target.value;
                      const newValue = inputValue
                        ? Number(inputValue.replace(/,/g, ""))
                        : 0;
                      handlePriceBlur("Item_PriceA", newValue);
                    }}
                    formatter={formatterWithCommas}
                    parser={parserStrict}
                    precision={2}
                    inputMode="decimal"
                    onKeyDown={onlyNumberKeyDown}
                    style={{ width: "100%" }}
                  />
                </div>

                <div className="pm-col">
                  <label
                    className="label-modal"
                    style={{
                      width: "90px",
                      display: "flex",
                      justifyContent: "end",
                    }}
                  >
                    ราคา 3
                  </label>
                  <InputNumber
                    value={
                      data.Item_PriceB === "" ? null : Number(data.Item_PriceB)
                    }
                    onChange={(v) =>
                      setData((s) => ({ ...s, Item_PriceB: v ?? "" }))
                    }
                    onFocus={(e) => {
                      headerPriceBeforeChangeRef.current.Item_PriceB = Number(
                        data.Item_PriceB ?? 0
                      );
                      rowPricesBeforeChangeRef.current = rows
                        .filter((r) => !r.__deleted)
                        .map((r) => ({ key: r.key, value: r.Price3 ?? 0 }));
                      selectOnFocus(e);
                    }}
                    onBlur={(e) => {
                      const inputValue = e.target.value;
                      const newValue = inputValue
                        ? Number(inputValue.replace(/,/g, ""))
                        : 0;
                      handlePriceBlur("Item_PriceB", newValue);
                    }}
                    formatter={formatterWithCommas}
                    parser={parserStrict}
                    precision={2}
                    inputMode="decimal"
                    onKeyDown={onlyNumberKeyDown}
                    style={{ width: "100%" }}
                  />
                </div>
                </div>
              )}
            </div>
                </>
              )}
          </div>

          {/* PREVIEW TABLE */}
          <div className="pm-table-wrap" ref={tableWrapRef}>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <Button
                size="small"
                type="primary"
                onClick={handleAddQuickRow}
              >
                เพิ่มรายการสินค้า
              </Button>
            </div>
            <Table
              size="small"
              rowKey="key"
              columns={columns}
              dataSource={rows.filter((r) => !r.__deleted)}
              pagination={{
                pageSize: 50,
                showSizeChanger: true,
                pageSizeOptions: ["60", "80", "100", "200"],
                // showTotal: (total, range) => (
                //   <div
                //     style={{
                //       position: "absolute",
                //       left: 0,
                //       fontSize: "14px",
                //       color: "#666",
                //     }}
                //   >
                //     ทั้งหมด {total} รายการ
                //   </div>
                // ),
                position: ["bottomRight"],
                style: {
                  position: "relative",
                  paddingTop: "8px",
                },
              }}
              bordered
              sticky
              scroll={{ x: totalColumnWidth, y: 450 }}
              rowClassName={(record) => {
                if (record.__isNew) return "row-new";
                return "";
              }}
              locale={{
                emptyText: (
                  <div style={{ padding: "40px 0", color: "#999" }}>
                    <div style={{ fontSize: "16px", marginBottom: "8px" }}>
                      ไม่พบข้อมูล
                    </div>
                  </div>
                ),
              }}
            />
          </div>
          <div
            style={{
              color: "black",
              padding: "8px 12px",
              display: "flex",
              alignItems: "center",
              gap: "20px",
              justifyContent: "space-between",
            }}
          >
            <span>รายการ : {rows?.length ? rows?.length : 0}</span>
            <span style={{ fontSize: "12px", color: "#666" }}>
              💡 ใช้ลูกศร ↑↓ เปลี่ยนแถว | ←→ เปลี่ยนราคา
            </span>
          </div>
        </div>

        {/* FOOTER (ไม่มีปุ่มย้อนกลับ) */}
        <div className="pm-footer">
          <div className="spacer" />
          {mode === "edit" && selectedRowKeys.length > 0 && (
            <button
              type="button"
              className="pm-del"
              onClick={handleDeleteSelected}
            >
              ลบรายการที่เลือก ({selectedRowKeys.length})
            </button>
          )}
          <button className="pm-save" type="button" onClick={submitAll}>
            บันทึก
          </button>
        </div>
      </div>

      {/* ไม่แสดง Modal ตารางข้อมูลซ้ำแล้ว แสดงแค่ alert */}

      <Modal
        open={bulkUpdateInfo.open}
        title={(() => {
          const changedFields = [];
          if (bulkUpdateInfo.changes.Item_Group) changedFields.push("หมวดหมู่");
          if (bulkUpdateInfo.changes.Item_Name) changedFields.push("ชื่อ");
          if (bulkUpdateInfo.changes.Item_Brand) changedFields.push("ยี่ห้อ");
          if (bulkUpdateInfo.changes.Item_Series) changedFields.push("รุ่น");
          return `มีการแก้ข้อมูล ${changedFields.join(" ,")}`;
        })()}
        onCancel={() =>
          setBulkUpdateInfo({ open: false, changes: {}, affectedItems: [] })
        }
        width={1000}
        footer={null}
      >
        <div style={{ marginBottom: 16 }}>
          {/* ✅ Before/After Comparison */}
          <div
            style={{
              marginBottom: 16,
              padding: 12,
              background: "#fff7ed",
              borderRadius: 4,
              fontSize: 14,
            }}
          >
            <div style={{ marginBottom: 8 }}>
              <strong>จาก :</strong>{" "}
              <span>
                {(() => {
                  const original = originalHeaderDataRef.current || {};
                  const parts = [];

                  if (bulkUpdateInfo.changes.Item_Group) {
                    const grpName =
                      groups.find(
                        (g) => String(g.id) === String(original.Item_Group)
                      )?.name || "";
                    parts.push(`หมวดหมู่ : ${grpName}`);
                  }
                  if (bulkUpdateInfo.changes.Item_Name) {
                    parts.push(`ชื่อ : ${original.Item_Name || ""}`);
                  }
                  if (bulkUpdateInfo.changes.Item_Brand) {
                    parts.push(`ยี่ห้อ : ${original.Item_Brand || ""}`);
                  }
                  if (bulkUpdateInfo.changes.Item_Series) {
                    parts.push(`รุ่น : ${original.Item_Series || ""}`);
                  }

                  return parts.join("  ");
                })()}
              </span>
            </div>
            <div>
              <strong>ไปเป็น :</strong>{" "}
              <span style={{ color: "#E13F57" }}>
                {(() => {
                  const parts = [];

                  if (bulkUpdateInfo.changes.Item_Group) {
                    const grpName =
                      groups.find(
                        (g) =>
                          String(g.id) ===
                          String(bulkUpdateInfo.changes.Item_Group)
                      )?.name || "";
                    parts.push(`หมวดหมู่ : ${grpName}`);
                  }
                  if (bulkUpdateInfo.changes.Item_Name) {
                    parts.push(`ชื่อ : ${bulkUpdateInfo.changes.Item_Name}`);
                  }
                  if (bulkUpdateInfo.changes.Item_Brand) {
                    parts.push(`ยี่ห้อ : ${bulkUpdateInfo.changes.Item_Brand}`);
                  }
                  if (bulkUpdateInfo.changes.Item_Series) {
                    parts.push(`รุ่น : ${bulkUpdateInfo.changes.Item_Series}`);
                  }

                  return parts.join("  ");
                })()}
              </span>
            </div>
          </div>

          {/* ✅ ข้อความเหนือตาราง */}
          <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 500 }}>
            ข้อมูลสินค้าใหม่จะเปลี่ยนแปลงทุกรายการดังนี้
          </div>
        </div>

        <Table
          size="small"
          rowKey="Item_Id"
          pagination={{ pageSize: 10 }}
          dataSource={bulkUpdateInfo.affectedItems}
          scroll={{ y: 400 }}
          columns={[
            { title: "#", width: 50, render: (_, __, i) => i + 1 },
            { title: "รหัส", dataIndex: "Item_Code", width: 140 },
            { title: "ชื่อ", dataIndex: "Item_Name", width: 260 },
            { title: "ยี่ห้อ", dataIndex: "Item_Brand", width: 120 },
            { title: "รุ่น", dataIndex: "Item_Series", width: 120 },
          ]}
        />

        <div
          style={{
            marginTop: 16,
            display: "flex",
            gap: 12,
            justifyContent: "flex-end",
          }}
        >
          <Button
            onClick={() =>
              setBulkUpdateInfo({ open: false, changes: {}, affectedItems: [] })
            }
          >
            ยกเลิก
          </Button>
          <Button
            type="primary"
            onClick={async () => {
              setIsLoading(true);
              setBulkUpdateInfo({
                open: false,
                changes: {},
                affectedItems: [],
              });

              try {
                await bulkUpdateByCodeMaster(
                  bulkUpdateInfo.changes,
                  itemBaseCode,
                  {
                    silent: true,
                  }
                );
                const { toInsert, toUpdate, toDelete } = splitChanges(rows);

                if (toDelete.length > 0) {
                  const check = await deleteItems(toDelete, {
                    checkOnly: true,
                  });
                  const canDelete = check?.canDelete || [];
                  if (canDelete.length > 0) {
                    await deleteItems(canDelete, { silent: true });
                  }
                }

                if (toUpdate.length > 0) {
                  await updateItems(toUpdate);
                }

                if (toInsert.length > 0) {
                  const whForEdit = initialData?.header?.WH_Ids ?? whIds ?? [];
                  const payload = {
                    mode: "create",
                    header: buildHeader(
                      data,
                      data.Item_Name,
                      whForEdit,
                      initQty,
                      itemBaseCode
                    ),
                    details: toInsert.map((r) => mapRowToDetail(r, whForEdit)),
                  };
                  await submitItems(payload);
                }

                setOpen(false);
                await onDataChanged?.();
              } catch (e) {
                console.error(e);
                getAlert("FAILED", "บันทึกไม่สำเร็จ");
              } finally {
                setIsLoading(false);
              }
            }}
          >
            ยืนยัน
          </Button>
        </div>
      </Modal>

      {/* Modal สำหรับเพิ่มหมวดหมู่และหน่วย */}
      <AddCategoryUnitModal
        open={showAddCategoryUnitModal}
        onClose={() => setShowAddCategoryUnitModal(false)}
        onSuccess={async (type, payload) => {
          if (type === "category") {
            const refreshed = await refreshGroups();
            const rawId = payload?.id;
            const normalizedId =
              typeof rawId === "object" && rawId !== null
                ? rawId.Group_Id ?? rawId.id ?? rawId.ID ?? rawId.value
                : rawId;
            const newId =
              normalizedId ??
              refreshed?.find(
                (g) =>
                  String(g.code || "").toLowerCase() ===
                    String(payload?.code || "").toLowerCase() ||
                  String(g.name || "").toLowerCase() ===
                    String(payload?.name || "").toLowerCase()
              )?.id;

            if (newId) {
              setData((prev) => ({ ...prev, Item_Group: String(newId) }));
            } else {
              setData((prev) => ({ ...prev, Item_Group: "" }));
            }
          }
          // หน่วยจะถูก refresh และแสดงใน modal อัตโนมัติ
          setShowAddCategoryUnitModal(false);
        }}
        existingGroups={groups}
        existingUnits={units}
        onUnitsRefresh={refreshUnits}
      />

      <Modal
        open={showAddUnitModal}
        title="เพิ่มหน่วยใหม่"
        onCancel={() => setShowAddUnitModal(false)}
        onOk={handleCreateUnit}
        confirmLoading={isSavingUnit}
        okText="บันทึก"
        cancelText="ยกเลิก"
        width={420}
        destroyOnClose
        zIndex={10600}
      >
        <div style={{ marginTop: 12 }}>
          <label className="form-label" style={{ display: "block" }}>
            ชื่อหน่วย<span style={{ color: "red" }}>*</span>
          </label>
          <Input
            value={newUnitName}
            onChange={(e) => setNewUnitName(e.target.value)}
            placeholder="เช่น ชิ้น, กล่อง, โหล"
            maxLength={50}
            style={{ height: 36 }}
          />
        </div>
      </Modal>

      {/* Modal สำหรับเพิ่มคลัง */}
      <AddWarehouseModal
        open={showAddWarehouseModal}
        onClose={() => setShowAddWarehouseModal(false)}
        onSuccess={async (newWHId) => {
          await refreshWarehouses();
          setWhIds([newWHId]);
          setShowAddWarehouseModal(false);
        }}
        existingWarehouses={warehouses}
      />

      <style jsx global>{`
        /* ====== Scrollbar & layout helpers (เดิม) ====== */
        .items-table .ant-table-content,
        .items-table .ant-table-body {
          overflow: auto !important;
          scrollbar-gutter: stable both-edges;
          scrollbar-width: thin;
        }
        .items-table .ant-table-content::-webkit-scrollbar,
        .items-table .ant-table-body::-webkit-scrollbar {
          height: 10px;
          width: 10px;
        }
        .items-table .ant-table-content::-webkit-scrollbar-thumb,
        .items-table .ant-table-body::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.3);
          border-radius: 8px;
        }

        .label-modal {
          width: 150px;
        }
        .label-modal2 {
          width: 30px;
        }
        .ant-table-wrapper .ant-table-thead > tr > th {
          background: linear-gradient(90deg, #E13F57, #E13F57);
          color: #fff;
          font-weight: 600;
        }

        /* ====== แถบสีแถว/โฮเวอร์/เลือก (เดิม) ====== */
        .ant-table-tbody > tr.row-even > td {
          background: #ffffff;
        }
        .ant-table-tbody > tr.row-odd > td {
          background: oklch(0.98 0.01 71.17);
        }
        .ant-table-tbody > tr:hover > td,
        .ant-table-tbody > tr.ant-table-row-selected > td {
          background: oklch(0.91 0.05 60.5) !important;
        }

        /* ====== Compact AntD Table inside ProductModal ====== */
        .pm-table-wrap {
          --pm-row-h: 26px; /* ความสูงแถวข้อมูล */
          --pm-head-h: 40px; /* ความสูงหัวตาราง */
          --pm-pad-y: 0px; /* padding แนวตั้ง */
          --pm-pad-x: 0px; /* padding แนวนอน */
          --pm-font: 12px; /* ขนาดตัวอักษร */
        }
        .pm-table-wrap .ant-table {
          --pm-row-h: 26px; /* ความสูงแถวข้อมูล */
          --pm-head-h: 40px; /* ความสูงหัวตาราง */
          --pm-pad-y: 0px; /* padding แนวตั้ง */
          --pm-pad-x: 0px; /* padding แนวนอน */
          --pm-font: 12px; /* ขนาดตัวอักษร */
        }

        /* คุมที่ cell (ไม่เซ็ต height บน tr เพื่อไม่ชน measure-row) */
        .pm-table-wrap .ant-table-thead > tr > th {
          padding: 8px 8px !important;
          font-size: 13px;
          white-space: nowrap;
          vertical-align: middle;
          text-align: center;
        }
        /* body */

        /* 🔧 ซ่อนแถววัดขนาด/placeholder ของ AntD ที่ทำให้เหมือนมีแถวแรกแถม */
        .pm-table-wrap .ant-table-tbody > tr.ant-table-measure-row,
        .pm-table-wrap .ant-table-tbody > tr.ant-table-placeholder {
          height: 0 !important;
          line-height: 0 !important;
          font-size: 0 !important;
        }
        .pm-table-wrap .ant-table-tbody > tr.ant-table-measure-row > td,
        .pm-table-wrap .ant-table-tbody > tr.ant-table-placeholder > td {
          padding: 0 !important;
          border: 0 !important;
        }

        /* คุมความสูงคอมโพเนนต์อินพุตในเซลล์ให้พอดีแถว */
        .pm-table-wrap .ant-input-number,
        .pm-table-wrap .ant-input-number input {
          height: calc(var(--pm-row-h) - 2px) !important;
          line-height: calc(var(--pm-row-h) - 2px) !important;
          padding-top: 0 !important;
          padding-bottom: 0 !important;
        }
        .pm-table-wrap .ant-input-number-input {
          text-align: right !important;
        }
        .pm-table-wrap
          .ant-select-single:not(.ant-select-customize-input) {
          height: 26px
          height: 100% !important;
        }
        .pm-table-wrap
          .ant-select-single:not(.ant-select-customize-input)
          .ant-select-selector {
          min-height: 100% !important;
          height: 100% !important;
          align-items: center !important;
          padding: 0 8px !important;
        }
        .pm-table-wrap
          .ant-select-single:not(.ant-select-customize-input)
          .ant-select-selection-item,
        .pm-table-wrap
          .ant-select-single:not(.ant-select-customize-input)
          .ant-select-selection-placeholder {
          line-height: normal !important;
          display: flex !important;
          align-items: center !important;
          height: 100% !important;
        }
        .pm-table-wrap
          .ant-select-single:not(.ant-select-customize-input)
          .ant-select-selection-search {
          inset: 0 !important;
          height: 100% !important;
        }
        .pm-table-wrap
          .ant-select-single:not(.ant-select-customize-input)
          .ant-select-selection-search-input {
          height: 100% !important;
        }
        .pm-table-wrap .ant-input-number-handler-wrap {
          height: 100% !important;
        }

        /* ไอคอนลบให้สมส่วนกับความสูงแถว */
        .pm-del-icon {
          font-size: 14px !important;
          line-height: var(--pm-row-h) !important;
        }

        /* ====== Chips/Buttons/Effect (เดิม) ====== */
        .pm-chip {
          position: relative;
          display: inline-flex;
          align-items: center;
          padding: 3px 0;
          border-radius: 16px;
          background-color: #f2f2f2;
          margin: 4px;
          width: 80px;
          transition: background-color 0.2s ease;
          white-space: nowrap;
          justify-content: center;
        }
        .pm-pick-btn {
          width: 90px;
        }
        .pm-chip-row {
          display: grid;
          grid-template-columns: auto auto auto;
          position: relative;
        }
        .pm-chip-row2 {
          display: grid;
          position: relative;
          gap: 5px;
        }
        .pm-chip:hover {
          background-color: #e0e0e0;
        }
        .pm-chip-remove {
          position: absolute;
          top: -4px;
          right: -4px;
          background: #979191ff;
          color: #fff;
          border: none;
          border-radius: 50%;
          width: 20px;
          height: 20px;
          font-size: 12px;
          cursor: pointer;
          display: none;
        }
        .pm-chip:hover .pm-chip-remove {
          display: block;
        }

        .pm-row-focus > td {
          background: #fff8e1 !important;
        }

        /* แถวที่ซ้ำ */
        .pm-row-dup > td {
          background: #fee2e2 !important; /* แดงอ่อน */
          border-top: 1px solid #fecaca !important;
          border-bottom: 1px solid #fecaca !important;
        }

        @keyframes pmBlink {
          0% {
            box-shadow: inset 0 0 0 0 rgba(225, 63, 87, 0.6);
          }
          50% {
            box-shadow: inset 0 0 0 9999px rgba(225, 63, 87, 0.15);
          }
          100% {
            box-shadow: inset 0 0 0 0 rgba(225, 63, 87, 0);
          }
        }
        .pm-row-blink > td {
          animation: pmBlink 1.2s ease;
        }
      `}</style>
    </div>,
    document.body
  );
}
